import {normalizeBuyerName,validateBuyerName} from "./utils/buyerName";
import {restoreBuyerProfile} from "./utils/restoreBuyerProfile";
import { isChatNotification } from "./utils/buyerNotifications";
import { buyerOrderQueryUrl, readBuyerOrderEntry } from "./utils/buyerOrderNavigation";
import { initProductCameraRecovery } from "./utils/productCamera";
import { readProductCameraDraft, clearProductCameraDraft } from "./utils/productCameraDraft";
import {prepareRecoveryPkce,readRecoveryPkce,clearRecoveryPkce,recoveryCodeFromUrl} from "./utils/recoveryPkce";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { prepareSignupPkce, readSignupPkce, clearSignupPkce, signupCodeFromUrl } from "./utils/signupPkce";
import {prepareNativeNotificationRead} from "./utils/nativeBadge";
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import AppLayout from "./components/AppLayout";
import BetaFeedback from "./components/BetaFeedback";
import FeedbackUpdates from "./components/FeedbackUpdates";
import NetworkStatusBanner from "./components/NetworkStatusBanner";
import NativePushBridge, { deactivateNativePush } from "./components/NativePushBridge";


import { DB, SB, KEY } from "./api/supabase";
import { ActionIcon, Btn } from "./components/ui";

import { fetchBuyerDashboardStats, fetchBuyerOrdersPage, fetchBuyerOrderStats, fetchVendorPortalMe, fetchBuyerPaymentOrders } from "./api/dworksApi";
import { DWORKS_API_BASE, DWORKS_FREE_LAUNCH_MODE, DWORKS_STORE_BUILD } from "./constants/dworksConfig";
import { getUserErrorMessage } from "./utils/appError";
import { validatePasswordPolicy, getWeakPasswordGuidance } from "./utils/passwordPolicy";
import { formatPhoneNumber, normalizePhoneNumber } from "./utils/contactFormat";

const DashPage = React.lazy(() => import("./pages/DashPage"));
const VendorPage = React.lazy(() => import("./pages/VendorPage"));
const MorePage = React.lazy(() => import("./pages/MorePage"));
const SubscriptionPage = React.lazy(() => import("./pages/SubscriptionPage"));
const BuyerGuidePage = React.lazy(() => import("./pages/BuyerGuidePage"));
const BuyerFaqPage = React.lazy(() => import("./pages/BuyerFaqPage"));
const SettingsPage = React.lazy(() => import("./pages/SettingsPage"));
const ListPage = React.lazy(() => import("./pages/ListPage"));
const ProdsPage = React.lazy(() => import("./pages/ProdsPage"));
const OrderPage = React.lazy(() => import("./pages/OrderPage"));
const InventoryPage = React.lazy(() => import("./pages/InventoryPage"));
const VendorConfirmPage = React.lazy(() => import("./pages/VendorConfirmPage"));
const NotificationsPage = React.lazy(() => import("./pages/NotificationsPage"));
const loadChatPage = () => import("./pages/ChatPage");
const ChatPage = React.lazy(loadChatPage);
const VendorLoginPage = React.lazy(() => import("./pages/VendorLoginPage"));
const VendorJoinPage = React.lazy(() => import("./pages/VendorJoinPage"));
const VendorSignupPage = React.lazy(() => import("./pages/VendorSignupPage"));
const VendorStaffJoinPage = React.lazy(() => import("./pages/VendorStaffJoinPage"));
const OpsDashboardPage = React.lazy(() => import("./pages/OpsDashboardV2Page"));
const OpsBroadcastPage = React.lazy(() => import("./pages/OpsBroadcastPage"));
const OpsUsersPage = React.lazy(() => import("./pages/OpsUsersPage"));
const FactoryPortalEntry = React.lazy(() => import("./pages/FactoryPortalEntry"));
const BuyerAuthView = React.lazy(() => import("./pages/BuyerAuthView"));
const BuyerEmailFindView = React.lazy(() => import("./pages/BuyerEmailFindView"));
const BuyerIntroView = React.lazy(() => import("./pages/BuyerIntroView"));
const BuyerPasswordResetView = React.lazy(() => import("./pages/BuyerPasswordResetView"));
const PlansPage = !DWORKS_STORE_BUILD ? React.lazy(() => import("./pages/PlansPage")) : null;
const VendorPortalPage = React.lazy(() => import("./pages/VendorPortalPage"));

const SignupLink = registerPlugin("SignupLink");
let signupExchange = null;
function exchangeSignupOnce(code,verifier){
  if(signupExchange?.code!==code) signupExchange={code,promise:DB.exchangeSignupCode(code,verifier)};
  return signupExchange.promise;
}
const DWORKS_PRIVACY_URL = "https://d-works.kr/privacy";
const DWORKS_TERMS_URL = "https://d-works.kr/terms";
const DWORKS_KAKAO_URL = "https://pf.kakao.com/_xfxgwbX";

const ACCOUNT_BLOCKED_STATUSES = ["deleted", "account_deleted", "account_disabled", "blocked"];
const BILLING_ENABLED = !DWORKS_FREE_LAUNCH_MODE && !DWORKS_STORE_BUILD;
const SESSION_REFRESH_BUFFER_MS = 2 * 60 * 1000;

function buyerPageNeedsBootstrap(pageKey,targetOrderId=""){
  if(pageKey==="notifications" || pageKey==="ops" || pageKey==="ops-users" || pageKey==="ops-broadcast") return false;
  if(pageKey==="chat") return Boolean(String(targetOrderId||"").trim());
  return true;
}

function getKoreaDateKey(){
  return new Intl.DateTimeFormat("en-CA",{
    timeZone:"Asia/Seoul",
    year:"numeric",
    month:"2-digit",
    day:"2-digit"
  }).format(new Date());
}

function normalizeAuthSession(user, auth = {}) {
  const expiresAt = Number(auth.expires_at || auth.expiresAt || user?.expiresAt || 0);
  const expiresIn = Number(auth.expires_in || auth.expiresIn || 3600);
  return {
    ...user,
    name: normalizeBuyerName(user?.name),
    token: auth.access_token || auth.token || user?.token || "",
    refreshToken: auth.refresh_token || auth.refreshToken || user?.refreshToken || "",
    expiresAt: expiresAt || Math.floor(Date.now() / 1000) + expiresIn
  };
}

function sessionNeedsRefresh(user) {
  if (!user?.refreshToken) return false;
  const expiresAtMs = Number(user.expiresAt || 0) * 1000;
  return !expiresAtMs || expiresAtMs - Date.now() <= SESSION_REFRESH_BUFFER_MS;
}

async function refreshStoredSession(user, sessionKey) {
  if (!user?.refreshToken) throw Object.assign(new Error("로그인 상태를 확인할 수 없습니다. 다시 로그인해 주세요."),{code:"SESSION_RELOGIN_REQUIRED"});
  const refreshed = await DB.refreshSession(user.refreshToken);
  if (!refreshed?.access_token || !refreshed?.refresh_token) {
    throw Object.assign(new Error("로그인 상태를 갱신하지 못했습니다. 다시 로그인해 주세요."),{code:[400,401,403].includes(Number(refreshed?.status))?"SESSION_RELOGIN_REQUIRED":"SESSION_REFRESH_UNAVAILABLE"});
  }
  const nextUser = normalizeAuthSession(user, refreshed);
  try {
    if (JSON.parse(localStorage.getItem(sessionKey) || "null")?.token === user.token) {
      localStorage.setItem(sessionKey, JSON.stringify(nextUser));
    }
  } catch {}
  return nextUser;
}
const BUYER_PAGE_PATHS = {
  dash: "/",
  order: "/order",
  prods: "/product",
  list: "/history",
  chat: "/chat",
  inventory: "/inventory",
  vendors: "/partners",
  settings: "/settings",
  more: "/more",
  subscription: "/subscription",
  faq: "/help/faq",
  guide: "/help/guide",
  ...(BILLING_ENABLED?{plans:"/plans"}:{}),
  notifications: "/notifications",
};
const BUYER_PATH_PAGES = Object.fromEntries(
  Object.entries(BUYER_PAGE_PATHS).map(([pageName, pathName]) => [
    pathName,
    pageName,
  ])
);
const AUTH_PATHS = new Set(["/intro", "/login", "/join", "/find-email", "/find", "/reset-password", "/verify-email"]);

function normalizePathname(pathname) {
  const normalized = String(pathname || "/").replace(/\/+$/, "");
  return normalized || "/";
}

function getOpsPageFromPath(pathname) {
  const path = normalizePathname(pathname);
  if (path === "/ops/users") return "ops-users";
  if (path === "/ops/broadcast") return "ops-broadcast";
  if (path === "/ops" || path === "/admin/ops" || path.startsWith("/ops/")) return "ops";
  return "";
}

function readSettingsSection(){
  const section=new URLSearchParams(window.location.search).get("section");
  return ["brand","factory","billing"].includes(section)?section:"account";
}

function getBuyerPageFromPath(pathname) {
  return getOpsPageFromPath(pathname) || (normalizePathname(pathname)==="/product/new"?"prods":BUYER_PATH_PAGES[normalizePathname(pathname)]) || "dash";
}

function getNotificationDeepLinkFromUrl() {
  if (typeof window === "undefined") return { orderId: "", threadId: "" };

  const pathName = normalizePathname(window.location.pathname);
  const params = new URLSearchParams(window.location.search || "");
  // `orderId` belongs to the Toss payment callback. Keep notification routing
  // isolated so opening an order notification never starts payment approval.
  const orderId = String(params.get("notificationOrderId") || "").trim();
  const threadId = String(params.get("threadId") || "").trim();

  return {
    orderId: pathName === "/history" ? orderId : "",
    threadId: pathName === "/chat" ? threadId : "",
  };
}

function getBuyerPageUrl(pageName, { orderId = "", threadId = "", section = "account" } = {}) {
  const basePath = BUYER_PAGE_PATHS[pageName] || "/";
  const params = new URLSearchParams();
  if (pageName === "list" && orderId) params.set("notificationOrderId", orderId);
  if (pageName === "settings" && section !== "account") params.set("section",section);
  if (pageName === "chat" && threadId) params.set("threadId", threadId);
  const query = params.toString();
  return query ? `${basePath}?${query}` : basePath;
}

function BuyerAppIcon({name,size=20}){
  const common={
    width:size,
    height:size,
    viewBox:"0 0 24 24",
    fill:"none",
    stroke:"currentColor",
    strokeWidth:1.8,
    strokeLinecap:"round",
    strokeLinejoin:"round",
    "aria-hidden":"true"
  };

  if(name==="dash") return <svg {...common}><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></svg>;
  if(name==="order") return <svg {...common}><path d="M9 5h6"/><path d="M9 3h6a2 2 0 0 1 2 2v1h2v15H5V6h2V5a2 2 0 0 1 2-2Z"/><path d="M12 11v6M9 14h6"/></svg>;
  if(name==="prods") return <svg {...common}><path d="m12 3 8 4-8 4-8-4 8-4Z"/><path d="m4 12 8 4 8-4"/><path d="m4 17 8 4 8-4"/></svg>;
  if(name==="list") return <svg {...common}><path d="M6 3h9l3 3v15H6z"/><path d="M14 3v4h4M9 11h6M9 15h6"/></svg>;
  if(name==="inventory") return <svg {...common}><path d="m4 7 8-4 8 4-8 4-8-4Z"/><path d="M4 7v10l8 4 8-4V7M12 11v10"/><path d="m8 5 8 4"/></svg>;
  if(name==="vendors") return <svg {...common}><circle cx="9" cy="8" r="3"/><path d="M3.5 19a5.5 5.5 0 0 1 11 0"/><path d="M16 5.5a3 3 0 0 1 0 5.8M17 14a5 5 0 0 1 3.5 5"/></svg>;
  if(name==="settings") return <svg {...common}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-1.6v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/></svg>;
  if(name==="bell") return <svg {...common}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>;
  return <svg {...common}><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
}

function isOpsAdminUser(user){
  return !DWORKS_STORE_BUILD && user?.accountType === "ops_admin";
}

const PAYMENT_QUERY_KEYS = [
  "paymentResult",
  "dworksOrderId",
  "paymentKey",
  "orderId",
  "amount",
  "code",
  "message"
];

const PRIVACY_COLLECTION_NOTICE = `D-Works는 회원가입 및 의류 발주·생산관리 서비스 제공을 위해 아래와 같이 개인정보와 서비스 이용 데이터를 수집·이용합니다. 본 안내는 회원가입 단계의 요약 고지이며, 자세한 내용은 공식 개인정보처리방침 및 이용약관을 따릅니다.

1. 수집·이용 목적
- D-Works 회원가입, 로그인, 계정 관리 및 중복 가입 방지
- 의류 발주, 상품, 공장, 원부자재처, 재고 관리 기능 제공
- 발주 저장, 발주내역 확인, 생산 요청 문구 생성, 재고 차감 및 변경 이력 관리
- 요금제, 결제, 무료 체험 사용량 관리
- 서비스 이용 이력, 변경 로그, 오류 확인, 고객 문의 응대 및 분쟁 대응
- 서비스 품질 개선, 사용 통계 분석, 발주·생산관리 리포트 제공
- 원단·부자재·공장·상품·발주 수량 등 데이터를 기반으로 한 통계/분석 기능 제공

2. 수집 항목
- 회원가입 필수 항목: 업체명, 성명, 직함, 연락처, 이메일, 비밀번호
- 회원가입 선택 항목: 브랜드명, 주소
- 서비스 이용 과정에서 생성·입력되는 정보: 상품명, 품목, 시즌, 컬러, 작업지시서 이미지, 원부자재명, 원단/부자재 종류, 색상, 소요량, 단가, 공정/기타 원가 항목, 공장명, 원부자재처 정보, 발주 수량, 발주 금액, 입고처, 발주 메모, 재고 수량, 발주·수정·삭제·상태 변경 이력
- 결제 및 요금제 정보: 요금제, 결제 금액, 결제 상태, 결제 식별값, 무료 체험 사용량
- 자동 생성 정보: 서비스 접속 기록, 기기 및 브라우저 정보, 알림 등록 정보, 활동 로그

3. 서비스 데이터 분석 및 리포트 활용
- D-Works는 서비스 개선 및 리포트 제공을 위해 발주·상품·원부자재·재고 데이터를 통계적으로 분석할 수 있습니다.
- 여러 사용자의 데이터를 함께 분석하는 경우 특정 업체, 브랜드, 담당자, 거래처, 공장, 원부자재처를 직접 식별할 수 없도록 집계 또는 비식별 처리한 형태로 활용합니다.
- 개별 사용자의 원본 발주 데이터, 거래처 정보, 단가 정보, 작업지시서 정보는 해당 사용자의 서비스 제공 및 관리 목적 범위 안에서 사용하며, 다른 이용자에게 공개하지 않습니다.

4. 처리위탁 및 외부 서비스 이용
- 서비스 운영을 위해 인증, 데이터베이스, 호스팅, API 서버, 결제, 알림 발송 등 일부 업무가 외부 서비스에 위탁될 수 있습니다.
- 세부 내용은 공식 개인정보처리방침의 개인정보 처리위탁, 국외 이전 가능성, 제3자 제공 항목을 따릅니다.

5. 보유 및 이용 기간
- 회원 탈퇴 시까지 보유·이용합니다.
- 단, 발주내역, 결제내역, 로그 등 관련 법령 또는 분쟁 대응을 위해 보관이 필요한 정보는 해당 법령 및 내부 보관 기준에 따라 일정 기간 보관할 수 있습니다.
- 중복 가입 및 부정 이용 방지를 위한 최소 정보는 탈퇴 후 일정 기간 보관 후 파기할 수 있습니다.

6. 동의 거부 권리 및 불이익
이용자는 개인정보 및 서비스 이용 데이터 수집·이용에 대한 동의를 거부할 권리가 있습니다.
다만 필수 항목 및 서비스 운영에 필요한 데이터 수집·이용에 동의하지 않을 경우 D-Works 회원가입 및 서비스 이용이 제한될 수 있습니다.`;

async function checkSignupPhoneAvailable(rawPhone){
  const normalized = normalizePhoneNumber(rawPhone);
  if(!normalized) return false;

  const res = await fetch(`${DWORKS_API_BASE}/api/auth/signup/phone-availability`, {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({phone:normalized,account_type:"buyer"})
  });

  const data = await res.json().catch(()=>null);

  if(!res.ok){
    if(res.status === 429){
      throw new Error("중복 확인 요청이 많습니다. 잠시 후 다시 시도해 주세요.");
    }

    throw new Error(
      data?.message ||
      "연락처 중복 확인에 실패했습니다. 잠시 후 다시 시도해 주세요."
    );
  }

  return data?.available === true;
}

function getSignupErrorMessage(error){
  const structuredMessage = [
    error?.code,
    error?.error,
    error?.message,
    error?.msg,
    error?.error_description,
  ].filter(Boolean).join(" ");
  const rawMessage = String(structuredMessage || error || "").trim();
  const lower = rawMessage.toLowerCase();

  if(lower.includes("dworks_rejoin_wait")){
    return rawMessage.includes("탈퇴 처리 중")
      ? "탈퇴 처리 중인 계정입니다. 처리가 완료된 후 다시 확인해 주세요."
      : "탈퇴 완료 후 1개월 동안은 동일한 이메일로 재가입할 수 없습니다.";
  }

  if(
    rawMessage.includes("DWORKS_DUPLICATE_PHONE") ||
    lower.includes("signup_phone_registry") ||
    (lower.includes("duplicate key") && lower.includes("normalized_tel"))
  ){
    return "이미 가입된 연락처입니다. 기존 계정으로 로그인하거나 고객센터로 문의해 주세요.";
  }

  if(lower.includes("weak_password") || lower.includes("known to be weak") || lower.includes("easy to guess")){
    return getWeakPasswordGuidance();
  }

  if(lower.includes("already")){
    return "이미 가입된 이메일입니다. 로그인해주세요.";
  }

  if(lower.includes("database error saving new user")){
    return "회원가입 처리 중 오류가 발생했습니다. 같은 연락처로 이미 가입한 이력이 있다면 기존 계정으로 로그인하거나 고객센터로 문의해 주세요.";
  }

  return rawMessage || "회원가입 처리 중 오류가 발생했습니다.";
}

function clearPaymentQueryParams(){
  if(typeof window === "undefined") return;

  const url = new URL(window.location.href);
  let touched = false;

  PAYMENT_QUERY_KEYS.forEach((key)=>{
    if(url.searchParams.has(key)){
      url.searchParams.delete(key);
      touched = true;
    }
  });

  if(touched){
    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  }
}

async function postPaymentConfirm({paymentKey,orderId,amount,token}){
  const headers={
    "Content-Type":"application/json"
  };

  if(token){
    headers.Authorization=`Bearer ${token}`;
  }

  const res = await fetch(`${DWORKS_API_BASE}/payments/confirm`, {
    method:"POST",
    headers,
    body:JSON.stringify({
      paymentKey,
      orderId,
      amount:Number(amount)
    })
  });

  const data = await res.json().catch(()=>null);

  if(!res.ok || data?.ok === false){
    throw new Error(data?.message || data?.error || "결제 승인 처리에 실패했습니다.");
  }

  return data;
}

async function postPaymentFail({orderId,code,message,token}){
  try{
    const headers={
      "Content-Type":"application/json"
    };

    if(token){
      headers.Authorization=`Bearer ${token}`;
    }

    await fetch(`${DWORKS_API_BASE}/payments/fail`, {
      method:"POST",
      headers,
      body:JSON.stringify({
        orderId:orderId || null,
        code:code || null,
        message:message || null
      })
    });
  }catch(error){
    console.warn("결제 실패 기록 전송 실패", error);
  }
}

function getPasswordResetRedirectUrl(){
  // 운영 기준 비밀번호 재설정 링크는 항상 실제 서비스 도메인으로 고정합니다.
  // dworks-mvp.vercel.app에서 테스트하더라도 메일 링크는 app.d-works.kr로 발송됩니다.
  return "https://app.d-works.kr/reset-password";
}

function getEmailVerificationRedirectUrl(){
  return "https://app.d-works.kr/login?emailConfirmed=1";
}

function EmailVerificationPage({email,onBack}){
  const [loading,setLoading]=useState(false);
  const [sent,setSent]=useState(false);
  const [error,setError]=useState("");
  const [cooldown,setCooldown]=useState(0);
  const resendInFlight=useRef(false);
  useEffect(()=>{
    if(cooldown<=0) return undefined;
    const timer=setTimeout(()=>setCooldown(value=>Math.max(0,value-1)),1000);
    return ()=>clearTimeout(timer);
  },[cooldown]);

  async function resend(){
    if(resendInFlight.current || cooldown>0) return;
    const cleanEmail=String(email||"").trim();
    if(!cleanEmail){
      setError("인증 메일을 받을 이메일을 확인할 수 없습니다. 회원가입을 다시 진행해 주세요.");
      return;
    }
    resendInFlight.current=true;
    setLoading(true);
    setError("");
    setSent(false);
    try{
      const result=await DB.resendSignup(cleanEmail,getEmailVerificationRedirectUrl(),await prepareSignupPkce(cleanEmail));
      if(result?.error || result?.code || result?.msg){
        const text=String(result.error_description || result.msg || result.message || result.error || result.code);
        if(result.status===429 || /rate.limit|too.many|security purposes/i.test(text)){
          setCooldown(60);
          throw new Error("메일 발송 제한에 걸렸습니다. 최소 60초 후 다시 시도해 주세요. 계속되면 고객센터로 문의해 주세요.");
        }
        throw new Error(text);
      }
      setCooldown(60);
      setSent(true);
    }catch(e){
      setError(getUserErrorMessage(e, "인증 메일 재발송에 실패했습니다. 잠시 후 다시 시도해 주세요."));
    }finally{
      resendInFlight.current=false;
      setLoading(false);
    }
  }

  return <BuyerPasswordResetView
    mode="verify"
    verificationEmail={email}
    resendSent={sent}
    resendCooldown={cooldown}
    error={error}
    loading={loading}
    onResend={resend}
    onBack={onBack}
  />;
}

function readPasswordRecoveryFromUrl(){
  if(typeof window === "undefined"){
    return {isRecovery:false, accessToken:"", refreshToken:"", error:"", errorDescription:""};
  }

  const searchParams = new URLSearchParams(window.location.search || "");
  const hashParams = new URLSearchParams(String(window.location.hash || "").replace(/^#/, ""));

  const type = hashParams.get("type") || searchParams.get("type") || "";
  const accessToken = hashParams.get("access_token") || searchParams.get("access_token") || "";
  const refreshToken = hashParams.get("refresh_token") || searchParams.get("refresh_token") || "";
  const error = hashParams.get("error") || searchParams.get("error") || "";
  const errorDescription = hashParams.get("error_description") || searchParams.get("error_description") || "";
  const resetFlag = searchParams.get("resetPassword") === "1";
  const resetPath = window.location.pathname === "/reset-password";
  const requestedAudience = searchParams.get("portal");
  const audience = ["vendor", "factory"].includes(requestedAudience) ? requestedAudience : "buyer";

  return {
    // 가입 확인 링크에도 access_token이 포함되므로, 토큰 존재만으로 비밀번호 재설정으로 판정하지 않습니다.
    isRecovery: type === "recovery" || resetFlag || resetPath,
    accessToken,
    refreshToken,
    error,
    errorDescription,
    audience
  };
}

function clearPasswordRecoveryUrl(){
  if(typeof window === "undefined") return;
  window.history.replaceState({}, document.title, "/");
}

async function sendPasswordResetEmail(email){
  const cleanEmail = String(email || "").trim();
  if(!cleanEmail){
    throw new Error("가입 이메일을 입력해 주세요.");
  }

  const redirectTo = getPasswordResetRedirectUrl();

  const res = await fetch(`${SB}/auth/v1/recover?redirect_to=${encodeURIComponent(redirectTo)}`, {
    method:"POST",
    headers:{
      apikey:KEY,
      Authorization:`Bearer ${KEY}`,
      "Content-Type":"application/json"
    },
    body:JSON.stringify({email:cleanEmail,...(Capacitor.isNativePlatform()?await prepareRecoveryPkce(cleanEmail):{})})
  });

  const data = await res.json().catch(()=>null);

  if(!res.ok){
    throw new Error(data?.msg || data?.error_description || data?.message || "비밀번호 재설정 메일 발송에 실패했습니다.");
  }

  return true;
}

async function findBuyerEmail({company,name,phone}){
  const res = await fetch(`${DWORKS_API_BASE}/api/buyer/auth/find-email`, {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    body:JSON.stringify({company,name,phone})
  });
  const data = await res.json().catch(()=>null);

  if(!res.ok){
    if(res.status === 429){
      throw new Error("이메일 찾기 요청이 많습니다. 잠시 후 다시 시도해 주세요.");
    }
    throw new Error(data?.message || "이메일 확인에 실패했습니다. 잠시 후 다시 시도해 주세요.");
  }

  return {
    found:data?.found === true,
    maskedEmail:String(data?.masked_email || "")
  };
}

async function updatePasswordWithRecoveryToken({accessToken,password}){
  if(!accessToken){
    throw new Error("비밀번호 재설정 인증 정보가 없습니다. 재설정 메일을 다시 요청해 주세요.");
  }

  const res = await fetch(`${SB}/auth/v1/user`, {
    method:"PUT",
    headers:{
      apikey:KEY,
      Authorization:`Bearer ${accessToken}`,
      "Content-Type":"application/json"
    },
    body:JSON.stringify({password})
  });

  const data = await res.json().catch(()=>null);

  if(!res.ok){
    throw new Error(data?.msg || data?.error_description || data?.message || "새 비밀번호 저장에 실패했습니다.");
  }

  return data;
}

function getFriendlyPasswordResetError(message){
  const raw = String(message || "").trim();
  const lower = raw.toLowerCase();

  if(!raw){
    return "새 비밀번호 저장에 실패했습니다.";
  }

  if(
    lower.includes("new password should be different") ||
    lower.includes("different from the old password") ||
    lower.includes("same password")
  ){
    return "기존 비밀번호와 다른 새 비밀번호를 입력해 주세요.";
  }

  if(
    lower.includes("password should be at least") ||
    lower.includes("password must be at least") ||
    lower.includes("weak password") ||
    lower.includes("weak_password") ||
    lower.includes("known to be weak") ||
    lower.includes("easy to guess")
  ){
    return getWeakPasswordGuidance("새 비밀번호");
  }

  if(
    lower.includes("expired") ||
    lower.includes("invalid") ||
    lower.includes("token") ||
    lower.includes("session")
  ){
    return "비밀번호 재설정 링크가 만료되었거나 유효하지 않습니다. 재설정 메일을 다시 요청해 주세요.";
  }

  return raw;
}

function isAccountBlockedStatus(status){
  return ACCOUNT_BLOCKED_STATUSES.includes(String(status || "").trim().toLowerCase());
}

async function getAccountAccessStatus(token,userId){
  if(!token || !userId || !DB.query){
    return {blocked:false,status:"unknown",plan:null,memo:""};
  }

  try{
    const qs=[
      "select=status,plan,memo,updated_at,user_id",
      `user_id=eq.${encodeURIComponent(userId)}`,
      "limit=1"
    ].join("&");

    const rows=await DB.query(token,"user_plans",qs);
    const planRow=Array.isArray(rows) && rows.length>0 ? rows[0] : null;
    const status=String(planRow?.status || "").trim().toLowerCase();

    return {
      blocked:isAccountBlockedStatus(status),
      status:status || "active",
      plan:planRow?.plan || null,
      memo:planRow?.memo || "",
      updatedAt:planRow?.updated_at || null
    };
  }catch(error){
    console.warn("계정 접근 상태 확인 실패", error);
    return {blocked:false,status:"check_failed",plan:null,memo:""};
  }
}

async function getAccountType(token,userId){
  if(!token || !userId || !DB.query) return "";

  try{
    const qs=[
      "select=account_type",
      `user_id=eq.${encodeURIComponent(userId)}`,
      "limit=1"
    ].join("&");
    const rows=await DB.query(token,"account_roles",qs);
    return String(Array.isArray(rows) ? rows[0]?.account_type || "" : "").trim().toLowerCase();
  }catch(error){
    console.warn("계정 역할 확인 실패", error);
    return "";
  }
}



// ── Splash 및 인증 ──
function AccountBlockedPage({user,accessInfo,onLogout}){
  return(
    <div >
      <header >
        <div >D-Works</div>
        <div >계정 이용 제한 안내</div>
      </header>

      <main >
        <section >
          <div  aria-hidden="true">
            !
          </div>

          <h1>탈퇴 처리된 계정입니다.</h1>

          <p >
            관리자 확인을 통해 회원 탈퇴 요청이 처리되어 D-Works 서비스 이용이 제한되었습니다.
            계정 복구나 처리 내역 확인이 필요하면 고객센터로 문의해 주세요.
          </p>

          <div >
            <span>계정</span>
            <b>{user?.email || "로그인 계정"}</b>
          </div>

          <div >
            실제 결제·거래·발주 기록은 관련 법령 및 서비스 운영 기준에 따라 일정 기간 보관될 수 있습니다.
            {accessInfo?.updatedAt ? `\n처리 기준일: ${String(accessInfo.updatedAt).slice(0,10)}` : ""}
          </div>

          <div >
            <Btn ch="로그아웃" v="w" sz="l" icon="arrow-left" iconPosition="start" onClick={onLogout}/>
            <a
              href={DWORKS_KAKAO_URL}
              target="_blank"
              rel="noopener noreferrer"

            >
              <ActionIcon name="chat"/>
              <span>고객센터 문의</span>
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}

function SplashPage({onStart}){
  const [slide,setSlide]=useState(0);
  const slides=[
    {title:"의류 발주 업무\n60분→3분!\n빠르고 간편하게!",desc:"수기계산 카카오톡 개별발주,\n이제 그만!",image:"/intro/intro-automation.png",alt:"의류 생산 발주 업무가 자동화되는 과정을 표현한 이미지"},
    {title:"원부자재 기반\n소요량 자동 계산",desc:"상품별 원부자재를 등록하면\n소요량이 자동 계산됩니다.",image:"/intro/intro-materials.png",alt:"의류 원부자재 소요량을 자동 계산하는 과정을 표현한 이미지"},
    {title:"거래처별\n원클릭 발송",desc:"업체별 발주서를 자동 생성하고\n카카오톡으로 즉시 전달합니다.",image:"/intro/intro-send.png",alt:"발주서가 여러 생산 거래처로 전달되는 과정을 표현한 이미지"},
    {title:"발주 이력\n데이터화",desc:"모든 발주가 DB에 저장되어\n언제든 조회 가능합니다.",image:"/intro/intro-history-v2.png",alt:"의류 발주 이력이 구조화된 데이터로 보관되는 과정을 표현한 이미지"}
  ];
  return(
    <BuyerIntroView
      slides={slides}
      slide={slide}
      onSlideChange={setSlide}
      onStart={onStart}
    />
  );
}

function BuyerEmailFindPage({onBack,onPasswordReset}){
  const [company,setCompany]=useState("");
  const [name,setName]=useState("");
  const [phone,setPhone]=useState("");
  const [result,setResult]=useState(null);
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);

  async function submit(){
    setErr("");
    setResult(null);

    if(!company.trim() || !name.trim() || normalizePhoneNumber(phone).length < 9){
      setErr("업체명, 가입자 성명, 가입 연락처를 정확히 입력해 주세요.");
      return;
    }

    setLoading(true);
    try{
      setResult(await findBuyerEmail({company,name,phone}));
    }catch(error){
      setErr(getUserErrorMessage(error, "이메일 확인에 실패했습니다. 잠시 후 다시 시도해 주세요."));
    }finally{
      setLoading(false);
    }
  }

  return(
    <BuyerEmailFindView
      company={company}
      onCompanyChange={setCompany}
      name={name}
      onNameChange={setName}
      phone={phone}
      onPhoneChange={setPhone}
      result={result}
      error={err}
      loading={loading}
      onSubmit={submit}
      onBack={onBack}
      onPasswordReset={onPasswordReset}
    />
  );
}

function PasswordResetRequestPage({initialEmail,onBack}){
  const [email,setEmail]=useState(String(initialEmail || "").trim());
  const [sent,setSent]=useState(false);
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);

  async function submit(){
    setErr("");

    if(!email.trim()){
      setErr("가입 이메일을 입력해 주세요.");
      return;
    }

    setLoading(true);
    try{
      await sendPasswordResetEmail(email);
      setSent(true);
    }catch(error){
      setErr(getUserErrorMessage(error, "비밀번호 재설정 메일 발송에 실패했습니다. 잠시 후 다시 시도해 주세요."));
    }finally{
      setLoading(false);
    }
  }

  return(
    <BuyerPasswordResetView
      mode="request"
      email={email}
      onEmailChange={setEmail}
      sent={sent}
      error={err}
      loading={loading}
      onSubmit={submit}
      onBack={onBack}
      kakaoUrl={DWORKS_KAKAO_URL}
    />
  );
}

function ResetPasswordPage({recoveryToken,recoveryError,onDone,onBack}){
  const [pw,setPw]=useState("");
  const [pw2,setPw2]=useState("");
  const [err,setErr]=useState(recoveryError || "");
  const [loading,setLoading]=useState(false);

  async function submit(){
    setErr("");

    if(!recoveryToken){
      setErr("비밀번호 재설정 링크가 만료되었거나 인증 정보가 없습니다. 재설정 메일을 다시 요청해 주세요.");
      return;
    }

    const passwordPolicyError=validatePasswordPolicy(pw,"새 비밀번호");
    if(passwordPolicyError){
      setErr(passwordPolicyError);
      return;
    }

    if(pw !== pw2){
      setErr("새 비밀번호가 일치하지 않습니다.");
      return;
    }

    setLoading(true);
    try{
      const changed=await updatePasswordWithRecoveryToken({accessToken:recoveryToken,password:pw});
      let fresh=null;
      try{if(changed?.email) fresh=await DB.signIn(changed.email,pw);}catch{}
      clearPasswordRecoveryUrl();
      if(fresh?.access_token && fresh?.refresh_token && fresh?.user){
        await onDone(fresh);
      }else{
        alert("비밀번호가 변경되었습니다. 다시 로그인해 주세요.");
        await onDone(null);
      }
    }catch(error){
      setErr(getFriendlyPasswordResetError(error?.message));
    }finally{
      setLoading(false);
    }
  }

  return(
    <BuyerPasswordResetView
      mode="update"
      password={pw}
      onPasswordChange={setPw}
      passwordConfirm={pw2}
      onPasswordConfirmChange={setPw2}
      error={err}
      loading={loading}
      onSubmit={submit}
      onBack={onBack}
    />
  );
}

function AuthPage({onLogin,authPath,onNavigate}){
  const [tab,setTab]=useState(authPath==="/join"?"up":"in");
  const [f,setF]=useState({company:"",brand:"",name:"",position:"",tel:"",email:"",pw:"",pw2:"",address:"",agree:false, agreeTerms:false, keepLoggedIn: true});
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);
  const sf=k=>v=>setF(p=>({...p,[k]:k==="tel"?formatPhoneNumber(v):v}));

  useEffect(()=>{
    setTab(authPath==="/join"?"up":"in");
    setErr("");
  },[authPath]);

  if(authPath==="/find-email"){
    return <BuyerEmailFindPage
      onBack={()=>onNavigate("/login")}
      onPasswordReset={()=>onNavigate("/find")}
    />;
  }

  if(authPath==="/find"){
    return <PasswordResetRequestPage initialEmail={f.email} onBack={()=>onNavigate("/login")}/>;
  }

  if(authPath==="/verify-email"){
    const verificationEmail=f.email || (typeof window!=="undefined" ? window.sessionStorage.getItem("dworksVerificationEmail")||"" : "");
    return <EmailVerificationPage email={verificationEmail} onBack={()=>onNavigate("/login")}/>;
  }

  async function submit(){
    setErr("");
    if(!f.email||!f.pw){setErr("이메일과 비밀번호를 입력해 주세요.");return;}
    const normalizedEmail=String(f.email||"").trim();
    if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)){
      setErr("올바른 이메일 주소를 입력해 주세요. 예: name@example.com");
      return;
    }

    const normalizedTel = normalizePhoneNumber(f.tel);
    const normalizedName = normalizeBuyerName(f.name);

    if(tab==="up"){
      if(!f.company||!normalizedName||!f.position||!f.tel){setErr("필수 항목을 입력하세요");return;}
      const nameError=validateBuyerName(normalizedName);
      if(nameError){setErr(nameError);return;}
      if(!/^010\d{8}$/.test(normalizedTel)){setErr("휴대폰 번호는 010으로 시작하는 11자리로 입력해 주세요.");return;}
      if(f.pw!==f.pw2){setErr("비밀번호가 일치하지 않습니다");return;}
      const passwordPolicyError=validatePasswordPolicy(f.pw);
      if(passwordPolicyError){setErr(passwordPolicyError);return;}
      if(!f.agree){setErr("개인정보 수집 및 이용에 동의해주세요");return;}
      if(!f.agreeTerms){setErr("이용약관에 동의해주세요");return;}
    }
    setLoading(true);
    try{
      if(tab==="up"){
        const exist = await DB.findProfileByEmail(f.email);
        if(Array.isArray(exist) && exist.length > 0){
          setErr("이미 가입된 이메일입니다. 로그인해주세요.");
          setTab("in");
          return;
        }

        try{
          const phoneAvailable = await checkSignupPhoneAvailable(f.tel);
          if(!phoneAvailable){
            setErr("이미 가입된 연락처입니다. 기존 계정으로 로그인하거나 고객센터로 문의해 주세요.");
            return;
          }
        }catch(error){
          setErr(getUserErrorMessage(error, "연락처 중복 확인에 실패했습니다. 잠시 후 다시 시도해 주세요."));
          return;
        }

        const r=await DB.signUp(
          f.email,
          f.pw,
          {
            company:f.company,
            brand:f.brand,
            name:normalizedName,
            position:f.position,
            tel:normalizedTel,
            tel_normalized:normalizedTel,
            address:f.address
          },
          getEmailVerificationRedirectUrl(),
          await prepareSignupPkce(f.email)
        );
        if(r?.error || r?.code || (r?.message && !r?.user)){
          setErr(getSignupErrorMessage(r));
          return;
        }
        const identities = r.user?.identities || [];
        if(identities.length === 0){
          setErr("이미 가입된 이메일입니다. 로그인해주세요.");
          setTab("in");
          return;
        }
        if(!r.access_token){
          window.sessionStorage.setItem("dworksVerificationEmail",f.email);
          onNavigate("/verify-email");
          return;
        }
        const r2=r;
        try{
          await DB.updateProfile(r2.access_token, r2.user.id, {
            name: normalizedName,
            company: f.company,
            tel: normalizedTel,
            email: f.email
          });
        }catch(e){}

        try{
          await DB.updateProfile(r2.access_token, r2.user.id, {
            tel_normalized: normalizedTel
          });
        }catch(e){
          console.warn("tel_normalized 저장 실패", e);
        }

        onLogin(normalizeAuthSession({token:r2.access_token,id:r2.user.id,...f,telNormalized:normalizedTel}, r2), f.keepLoggedIn);
      }else{
        const r=await DB.signIn(f.email,f.pw);
        if(!r.access_token){
          const authMessage=String(r?.error_description||r?.msg||r?.error||"");
          if(authMessage.toLowerCase().includes("email not confirmed")){
            window.sessionStorage.setItem("dworksVerificationEmail",f.email);
            onNavigate("/verify-email");
            return;
          }
          setErr("로그인 정보를 확인해 주세요. 이메일 또는 비밀번호가 올바르지 않습니다.");
          return;
        }
        let profile = null;
        try{
          const p = await DB.getProfile(r.access_token, r.user.id);
          profile = Array.isArray(p) && p.length > 0 ? p[0] : null;
        }catch(e){}
        const meta=r.user?.user_metadata||{};
        onLogin(normalizeAuthSession({token:r.access_token,id:r.user.id,name:profile?.name||meta.name||f.email.split("@")[0],company:profile?.company||meta.company||"",email:r.user.email,tel:profile?.tel||meta.tel||"",telNormalized:profile?.tel_normalized||meta.tel_normalized||"",brand:meta.brand||"",position:meta.position||"",address:meta.address||""}, r), f.keepLoggedIn);
      }
    }catch(e){
      console.warn("auth submit failed", e);
      setErr(getUserErrorMessage(e, "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요."));
    }
    finally{setLoading(false);}
  }

  return(
    <BuyerAuthView
      tab={tab}
      onTabChange={(nextTab)=>{
        setTab(nextTab);
        setErr("");
        onNavigate(nextTab==="up"?"/join":"/login");
      }}
      form={f}
      onFieldChange={sf}
      error={err}
      loading={loading}
      onSubmit={submit}
      onEmailFind={()=>onNavigate("/find-email")}
      onPasswordReset={()=>onNavigate("/find")}
      privacyNotice={PRIVACY_COLLECTION_NOTICE}
      privacyUrl={DWORKS_PRIVACY_URL}
      termsUrl={DWORKS_TERMS_URL}
      kakaoUrl={DWORKS_KAKAO_URL}
    />
  );
}

// ── 메인 앱 컴포넌트 ──
function AppPageFallback(){
  return(
    <div className="app-loading-screen" role="status" aria-live="polite">
      <span className="app-loading-screen__spinner" aria-hidden="true"/>
      <span className="app-loading-screen__label">필요한 화면을 불러오는 중입니다.</span>
    </div>
  );
}

export default function App(){
  useEffect(()=>{ void initProductCameraRecovery().catch(()=>{}); },[]);
  const isFactoryAreaPath = typeof window !== "undefined" && (window.location.pathname === "/factory" || window.location.pathname.startsWith("/factory/"));
  return(
    <React.Suspense fallback={<AppPageFallback/>}>
      {isFactoryAreaPath?<FactoryPortalEntry/>:<BuyerVendorApp/>}
    </React.Suspense>
  );
}

function BuyerVendorApp(){
  const [screen,setScreen]=useState("loading");
  const [user,setUser]=useState(null);
  const sessionRefreshPromiseRef=useRef(null);
  const currentSessionRef=useRef(null);
  currentSessionRef.current=user;

  async function refreshCurrentSession(){
    const sessionKey=user?.portalRole==="vendor" ? "dworks_vendor_session" : "dworks_session";
    if(!sessionRefreshPromiseRef.current){
      sessionRefreshPromiseRef.current=refreshStoredSession(user,sessionKey)
        .catch(error=>{
          if(error.code==="SESSION_RELOGIN_REQUIRED" && currentSessionRef.current?.token===user?.token){
            try{localStorage.removeItem(sessionKey);}catch{}
            setUser(current=>current?.id===user.id?null:current);
            const loginPath=user?.portalRole==="vendor"?"/vendor/login":"/login";
            setAuthPath(loginPath);setScreen(user?.portalRole==="vendor"?"vendor-auth":"auth");
            window.history.replaceState({},document.title,loginPath);
            alert("비밀번호가 변경되었거나 로그인 상태가 만료되었습니다. 다시 로그인해 주세요.");
          }
          throw error;
        })
        .finally(()=>{sessionRefreshPromiseRef.current=null;});
    }
    const nextUser=await sessionRefreshPromiseRef.current;
    setUser(current=>current?.id===nextUser.id && current?.token===user?.token ? {...current,...nextUser} : current);
    return nextUser;
  }
  const reconnectRefreshPromiseRef=useRef(null);
  const [authPath,setAuthPath]=useState(()=>
    typeof window==="undefined"?"/intro":normalizePathname(window.location.pathname)
  );
  const [page,setPage]=useState(()=>
    typeof window === "undefined"
      ? "dash"
      : getBuyerPageFromPath(window.location.pathname)
  );
  useLayoutEffect(()=>{
    // Route recovery before the first app frame is painted.
    if(screen!=="app" || !user?.id || user?.portalRole==="vendor") return;
    if(readProductCameraDraft(user.id)){
      setPage("prods");
      window.history.replaceState({},document.title,getBuyerPageUrl("prods"));
    }
  },[screen,user?.id,user?.portalRole]);
  const [productListResetKey,setProductListResetKey]=useState(0);
  const [productCreateRequest,setProductCreateRequest]=useState(null);
  const notificationOwnerRef=useRef(user?.id);
  notificationOwnerRef.current=user?.id;
  const [quickOrderRequest,setQuickOrderRequest]=useState(null);
  const [settingsSection,setSettingsSection]=useState(()=>readSettingsSection());
  const [vendors,setVendors]=useState([]);
  const [factories,setFactories]=useState([]);
  const [products,setProducts]=useState([]);
  const [orders,setOrders]=useState([]);
  const [orderPageInfo,setOrderPageInfo]=useState({offset:0,limit:30,total:0,has_more:false,next_offset:0});
  const [orderStats,setOrderStats]=useState(null);
  const [dashboardStats,setDashboardStats]=useState(null);
  const [recentOrderHistory,setRecentOrderHistory]=useState([]);
  const [ordersLoadingMore,setOrdersLoadingMore]=useState(false);
  const [ordersFiltering,setOrdersFiltering]=useState(false);
  const [ordersBootstrapLoading,setOrdersBootstrapLoading]=useState(false);
  const [ordersBootstrapError,setOrdersBootstrapError]=useState("");
  const buyerOrderQueryRef=useRef(readBuyerOrderEntry(window.location)||{status:"진행중",archived:"false",sort:"desc"});
  const [orderEntry,setOrderEntry]=useState(()=>readBuyerOrderEntry(window.location));
  const buyerOrderRequestSeqRef=useRef(0);
  const dashboardStatsRequestSeqRef=useRef(0);
  const buyerDataLoadPromiseRef=useRef(null);
  const buyerDataReadyRef=useRef(false);
  const [brandClients,setBrandClients]=useState([]);
  const [loading,setLoading]=useState(false);
  const [dataLoadError,setDataLoadError]=useState("");
  const [paymentConfirming,setPaymentConfirming]=useState(false);
  const [paymentNotice,setPaymentNotice]=useState("");
  const [resetRecoveryToken,setResetRecoveryToken]=useState("");
  const [resetRecoveryError,setResetRecoveryError]=useState("");
  const [resetRecoveryAudience,setResetRecoveryAudience]=useState("buyer");
  const [accountAccessInfo,setAccountAccessInfo]=useState(null);
  const [unreadNotificationCount,setUnreadNotificationCount]=useState(0);
  const [unreadOrderNotificationMap,setUnreadOrderNotificationMap]=useState({});
  const [unreadChatCount,setUnreadChatCount]=useState(0);
  const [notificationTargetOrderId,setNotificationTargetOrderId]=useState(()=>getNotificationDeepLinkFromUrl().orderId);
  const [notificationTargetThreadId,setNotificationTargetThreadId]=useState(()=>getNotificationDeepLinkFromUrl().threadId);
  const [vendorPortalReady,setVendorPortalReady]=useState(false);
  const [vendorRecoveryError,setVendorRecoveryError]=useState("");
  const paymentHandledRef=useRef(false);

  useEffect(()=>{
    const frame=window.requestAnimationFrame(()=>{
      window.scrollTo({top:0,left:0,behavior:"auto"});
      document.scrollingElement?.scrollTo({top:0,left:0,behavior:"auto"});
      document.querySelector(".app-content")?.scrollTo({top:0,left:0,behavior:"auto"});
    });

    return ()=>window.cancelAnimationFrame(frame);
  },[page,authPath,screen]);

  const vendorConfirmMatch =
    typeof window !== "undefined"
      ? window.location.pathname.match(/^\/vendor\/confirm\/([^/]+)\/?$/)
      : null;
  const vendorConfirmToken = vendorConfirmMatch ? decodeURIComponent(vendorConfirmMatch[1]) : "";
  const vendorJoinMatch =
    typeof window !== "undefined"
      ? window.location.pathname.match(/^\/vendor\/join\/([^/]+)\/?$/)
      : null;
  const vendorInviteToken = vendorJoinMatch ? decodeURIComponent(vendorJoinMatch[1]) : "";
  const vendorStaffJoinMatch =
    typeof window !== "undefined"
      ? window.location.pathname.match(/^\/vendor\/staff\/join\/([^/]+)\/?$/)
      : null;
  const vendorStaffInviteToken = vendorStaffJoinMatch ? decodeURIComponent(vendorStaffJoinMatch[1]) : "";
  const currentPath =
    typeof window !== "undefined"
      ? window.location.pathname.replace(/\/+$/, "") || "/"
      : "/";
  const isOpsPath = currentPath === "/ops" || currentPath === "/admin/ops" || currentPath.startsWith("/ops/");
  const isVendorLoginPath = currentPath === "/vendor/login";
  const isVendorSignupPath = currentPath === "/vendor/signup";
  const vendorPortalTabByPath = {
    "/vendor/portal": "orders",
    "/vendor/product": "products",
    "/vendor/chat": "chat",
    "/vendor/inventory": "inventory",
    "/vendor/settlements": "settlements",
    "/vendor/settings": "settings"
  };
  const vendorPortalInitialTab = vendorPortalTabByPath[currentPath] || "orders";
  const isVendorPortalPath = Boolean(vendorPortalTabByPath[currentPath]);
  const isVendorAreaPath = isVendorLoginPath || isVendorSignupPath || isVendorPortalPath;

  function navigateAuth(pathName,{replace=false}={}){
    const nextPath=AUTH_PATHS.has(pathName)?pathName:"/login";
    if(replace){
      window.history.replaceState({},document.title,nextPath);
    }else{
      window.history.pushState({},document.title,nextPath);
    }
    setAuthPath(nextPath);
    setScreen(nextPath==="/intro"?"splash":"auth");
  }

  function navigateBuyerPage(nextPage,section="account",orderQuery=null){
    const inOpsMode=isOpsPath || page==="ops" || page==="ops-users" || page==="ops-broadcast";
    if(inOpsMode){
      const safeOpsPage=["ops-users","ops-broadcast"].includes(nextPage)?nextPage:"ops";
      const nextPath=safeOpsPage==="ops-users"?"/ops/users":safeOpsPage==="ops-broadcast"?"/ops/broadcast":"/ops";
      setPage(safeOpsPage);
      if(normalizePathname(window.location.pathname)!==nextPath){
        window.history.pushState({},document.title,nextPath);
      }
      return;
    }
    const safePage=DWORKS_FREE_LAUNCH_MODE&&nextPage==="plans"?"settings":nextPage;
    if(safePage==="settings") setSettingsSection(section);
    if(safePage==="prods"&&page==="prods"){
      setProductListResetKey(current=>current+1);
      return;
    }
    const nextPath=safePage==="list"&&orderQuery?buyerOrderQueryUrl(orderQuery):getBuyerPageUrl(safePage,{section});
    if(safePage==="list") setOrderEntry(orderQuery);
    const activePath=`${normalizePathname(window.location.pathname)}${window.location.search||""}`;
    if(nextPath&&activePath!==nextPath){
      window.history.pushState({dworksPage:safePage},document.title,nextPath);
    }
    setPage(safePage);
  }

  function startQuickOrder(product){
    const productId=String(product?.id || "").trim();
    if(!productId) return;
    setQuickOrderRequest({productId,requestId:`${productId}:${Date.now()}`});
    navigateBuyerPage("order");
  }

  function navigateSettingsSection(nextSection){
    if(nextSection==="vendors" || nextSection==="inventory"){
      navigateBuyerPage(nextSection);
      return;
    }
    setSettingsSection(nextSection);
    navigateBuyerPage("settings",nextSection);
  }

  useEffect(()=>{
    const handlePopState=()=>{
      const pathName=normalizePathname(window.location.pathname);
      if(AUTH_PATHS.has(pathName)){
        if(user){
          window.history.replaceState({},document.title,"/");
          setPage("dash");
          setScreen("app");
          return;
        }
        setAuthPath(pathName);
        setScreen(pathName==="/intro"?"splash":pathName==="/reset-password"?"reset-password":"auth");
        return;
      }
      const nextOpsPage=getOpsPageFromPath(pathName);
      if(nextOpsPage){
        setPage(nextOpsPage);
        setScreen("app");
        return;
      }
      const nextPage=pathName==="/product/new"?"prods":BUYER_PATH_PAGES[pathName];
      if(nextPage){
        if(nextPage==="settings") setSettingsSection(readSettingsSection());
        if(nextPage==="list") setOrderEntry(readBuyerOrderEntry(window.location));
        const target=getNotificationDeepLinkFromUrl();
        setNotificationTargetOrderId(target.orderId);
        setNotificationTargetThreadId(target.threadId);
        setPage(nextPage);
      }
    };

    window.addEventListener("popstate",handlePopState);
    return ()=>window.removeEventListener("popstate",handlePopState);
  },[user]);

  useEffect(()=>{
    if(screen!=="app"||!user||isOpsPath||page==="ops"||page==="ops-users"||page==="ops-broadcast"||isVendorAreaPath||vendorConfirmToken||vendorInviteToken||vendorStaffInviteToken) return;

    const nextPath=page==="list"&&orderEntry&&!notificationTargetOrderId
      ? buyerOrderQueryUrl(orderEntry)
      : getBuyerPageUrl(page,{
      orderId:notificationTargetOrderId,
      threadId:notificationTargetThreadId,
      section:settingsSection
    });
    if(!nextPath) return;

    // The registration wizard owns its route and step history.
    if(page==="prods"&&normalizePathname(window.location.pathname)==="/product/new") return;
    const activePath=`${normalizePathname(window.location.pathname)}${window.location.search||""}`;
    if(activePath!==nextPath){
      window.history.pushState({},document.title,nextPath);
    }
  },[
    page,
    orderEntry,
    notificationTargetOrderId,
    notificationTargetThreadId,
    settingsSection,
    screen,
    user,
    isVendorAreaPath,
    vendorConfirmToken,
    vendorInviteToken,
    vendorStaffInviteToken
  ]);

  useEffect(()=>{
    const isBuyerApp=
      screen==="app" &&
      !!user &&
      !isOpsPath &&
      page!=="ops" &&
      page!=="ops-users" &&
      page!=="ops-broadcast" &&
      !isVendorAreaPath &&
      !vendorConfirmToken &&
      !vendorInviteToken &&
      !vendorStaffInviteToken;

    if(!isBuyerApp || buyerDataReadyRef.current) return;
    if(!buyerPageNeedsBootstrap(page,notificationTargetOrderId)) return;
    void loadData(user.token);
  },[
    screen,
    user?.token,
    page,
    orderEntry,
    notificationTargetOrderId,
    isOpsPath,
    isVendorAreaPath,
    vendorConfirmToken,
    vendorInviteToken,
    vendorStaffInviteToken
  ]);

  async function loadProductImage(productId){
    if(!user?.token||!productId) return "";
    const qs=[
      "select=id,image_url",
      `id=eq.${encodeURIComponent(productId)}`,
      "limit=1"
    ].join("&");
    const rows=await DB.query(user.token,"products",qs);
    if(!Array.isArray(rows)) throw new Error(rows?.message||"작업지시서를 불러오지 못했습니다.");
    const imageUrl=rows[0]?.image_url||"";
    setProducts(current=>current.map(product=>
      String(product.id)===String(productId)
        ? {...product,imageUrl,image_url:imageUrl,imageLoaded:true}
        : product
    ));
    return imageUrl;
  }

  async function loadDataOnce(token){
    const bootstrapQuery=buyerOrderQueryRef.current;
    const bootstrapOrderSeq=buyerOrderRequestSeqRef.current;
    const filteredSummary=Boolean(bootstrapQuery.startDate||bootstrapQuery.endDate||bootstrapQuery.brandClientId||bootstrapQuery.search);
    const dashboardRequestSeq=dashboardStatsRequestSeqRef.current+1;
    dashboardStatsRequestSeqRef.current=dashboardRequestSeq;
    setDashboardStats(null);
    setRecentOrderHistory([]);
    setLoading(true);
    setOrdersBootstrapLoading(true);
    setOrdersBootstrapError("");
    setDataLoadError("");
    try{
      // The dashboard summary is independent. Start it immediately instead of
      // waiting for every reference list and the first order page to finish.
      void fetchBuyerDashboardStats(token,getKoreaDateKey())
        .then(result=>{
          if(dashboardRequestSeq!==dashboardStatsRequestSeqRef.current) return;
          if(!filteredSummary && bootstrapOrderSeq===buyerOrderRequestSeqRef.current) setOrderStats(result?.order_stats||null);
          setDashboardStats(result?.stats||null);
          setRecentOrderHistory(Array.isArray(result?.stats?.recent_orders)?result.stats.recent_orders:[]);
        })
        .catch(async error=>{
          console.warn("발주·대시보드 통계 조회 실패",error);
          if(dashboardRequestSeq!==dashboardStatsRequestSeqRef.current) return;
          try{
            const fallback=await fetchBuyerOrdersPage(token,{
              archived:"false",
              limit:3,
              offset:0,
              sort:"desc"
            });
            if(dashboardRequestSeq!==dashboardStatsRequestSeqRef.current) return;
            setRecentOrderHistory(Array.isArray(fallback?.orders)?fallback.orders:[]);
          }catch(fallbackError){
            console.warn("최근 발주 대체 조회 실패",fallbackError);
          }
        });

      // Order history must not block already independent product/order entry data.
      const orderPagePromise=Promise.all([
        fetchBuyerOrdersPage(token,{...bootstrapQuery,limit:30,offset:0}),
        filteredSummary?fetchBuyerOrderStats(token,bootstrapQuery):Promise.resolve(null)
      ]).then(([value,summary])=>({value,summary}),error=>({error}));
      const[v,f,p,bc]=await Promise.all([
        DB.list(token,"vendors"),
        DB.list(token,"factories"),
        DB.query(token,"products","select=id,user_id,name,category,season,factory_id,factory,factory_tel,colors,bom,created_at,updated_at,color_bom,cost_items,brand_client_id&order=created_at.asc"),
        DB.list(token,"brand_clients")
      ]);
      if(dashboardRequestSeq!==dashboardStatsRequestSeqRef.current) return;

      const invalidResponse=[v,f,p,bc].find(value=>!Array.isArray(value));
      if(invalidResponse){
        const loadError=new Error(
          invalidResponse?.error_description||
          invalidResponse?.message||
          invalidResponse?.error||
          "핵심 데이터 응답 형식이 올바르지 않습니다."
        );
        loadError.status=Number(invalidResponse?.status||0);
        throw loadError;
      }

      setVendors(Array.isArray(v)?v.map(x=>({...x, subTel:x.sub_tel||"", address:x.address||"", bizNo:x.biz_no||"", memo:x.memo||""})):[]);
      setFactories(Array.isArray(f)?f.map(x=>({...x,bizType:x.biz_type||x.bizType||"", bizNo:x.biz_no||x.bizNo||"", memo:x.memo||""})):[]);
      setBrandClients(Array.isArray(bc)?bc.map(x=>({
        ...x,
        brandClientId:x.id,
        isDefault:!!x.is_default,
        isActive:x.is_active!==false
      })) : []);
      setProducts(Array.isArray(p)?p.map(x=>({
        ...x,
        factoryId:x.factory_id||x.factoryId||"",
        factoryTel:x.factory_tel||x.factoryTel||"",
        colors:x.colors||[],
        colorBom:x.color_bom||x.colorBom||{},
        imageUrl:"",
        imageLoaded:false,
        brandClientId:x.brand_client_id||x.brandClientId||""
      })):[]);
      setLoading(false);
      const orderResult=await orderPagePromise;
      if(dashboardRequestSeq!==dashboardStatsRequestSeqRef.current) return;
      buyerDataReadyRef.current=true;
      if(bootstrapOrderSeq!==buyerOrderRequestSeqRef.current) return;
      if(orderResult.error) {
        console.warn("발주 목록 조회 실패",orderResult.error);
        setOrdersBootstrapError("발주 목록을 불러오지 못했습니다. 다시 시도해 주세요.");
        return;
      }
      const o=orderResult.value;
      const orderRows=Array.isArray(o?.orders)?o.orders:[];
      setOrders(orderRows.map(x=>({
        ...x,
        is_archived:!!x.is_archived,
        archived_at:x.archived_at||null,
        brandClientId:x.brand_client_id||x.brandClientId||""
      })));
      buyerOrderQueryRef.current=bootstrapQuery;
      if(orderResult.summary) setOrderStats(orderResult.summary.stats||null);
      setOrderPageInfo(o?.page||{offset:0,limit:30,total:orderRows.length,has_more:false,next_offset:orderRows.length});
      buyerDataReadyRef.current=true;
    }catch(error){
      console.warn("핵심 데이터 조회 실패",error);
      if(dashboardRequestSeq===dashboardStatsRequestSeqRef.current) setDataLoadError(getUserErrorMessage(error,"발주 정보를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요."));
    }
    finally{if(dashboardRequestSeq===dashboardStatsRequestSeqRef.current) { setLoading(false); setOrdersBootstrapLoading(false); }}
  }

  function loadData(token){
    if(buyerDataLoadPromiseRef.current) return buyerDataLoadPromiseRef.current;
    const request=loadDataOnce(token).finally(()=>{
      if(buyerDataLoadPromiseRef.current===request) buyerDataLoadPromiseRef.current=null;
    });
    buyerDataLoadPromiseRef.current=request;
    return request;
  }



  async function refreshFeedbackOrderStats(){
    if(!user?.token) return;
    const token=user.token;
    const query=buyerOrderQueryRef.current;
    const result=await fetchBuyerOrderStats(token,{
      startDate:query.startDate,endDate:query.endDate,
      brandClientId:query.brandClientId,search:query.search
    });
    if(currentSessionRef.current?.token===token && buyerOrderQueryRef.current===query){
      setOrderStats(result?.stats||null);
    }
  }

  async function reloadBuyerOrders(filters={}){
    if(!user?.token) return;
    const normalized={
      sort:filters.sort==="asc"?"asc":"desc",
      status:filters.status||"",
      archived:filters.archived,
      startDate:filters.startDate||"",
      endDate:filters.endDate||"",
      brandClientId:filters.brandClientId||"",
      search:String(filters.search||"").trim()
    };
    const requestSeq=buyerOrderRequestSeqRef.current+1;
    buyerOrderRequestSeqRef.current=requestSeq;
    buyerOrderQueryRef.current=normalized;
    setOrdersFiltering(true);
    setDataLoadError("");
    try{
      const [pageResult,statsResult]=await Promise.all([
        fetchBuyerOrdersPage(user.token,{...normalized,limit:30,offset:0}),
        fetchBuyerOrderStats(user.token,{
          startDate:normalized.startDate,
          endDate:normalized.endDate,
          brandClientId:normalized.brandClientId,
          search:normalized.search
        })
      ]);
      if(requestSeq!==buyerOrderRequestSeqRef.current) return;
      const rows=Array.isArray(pageResult?.orders)?pageResult.orders.map(x=>({
        ...x,
        is_archived:!!x.is_archived,
        archived_at:x.archived_at||null,
        brandClientId:x.brand_client_id||x.brandClientId||""
      })):[];
      setOrders(rows);
      setOrderPageInfo(pageResult?.page||{offset:0,limit:30,total:rows.length,has_more:false,next_offset:rows.length});
      setOrderStats(statsResult?.stats||null);
    }catch(error){
      if(requestSeq!==buyerOrderRequestSeqRef.current) return;
      setDataLoadError(getUserErrorMessage(error,"조건에 맞는 발주 내역을 불러오지 못했습니다."));
    }finally{
      if(requestSeq===buyerOrderRequestSeqRef.current) setOrdersFiltering(false);
    }
  }

  async function loadMoreBuyerOrders(){
    if(!user?.token||ordersFiltering||ordersLoadingMore||!orderPageInfo?.has_more) return;
    setOrdersLoadingMore(true);
    try{
      const result=await fetchBuyerOrdersPage(user.token,{
        ...buyerOrderQueryRef.current,
        limit:orderPageInfo.limit||30,
        offset:orderPageInfo.next_offset||orders.length
      });
      const nextRows=Array.isArray(result?.orders)?result.orders.map(x=>({
        ...x,
        is_archived:!!x.is_archived,
        archived_at:x.archived_at||null,
        brandClientId:x.brand_client_id||x.brandClientId||""
      })):[];
      setOrders(current=>{
        const seen=new Set(current.map(order=>String(order.id)));
        return [...current,...nextRows.filter(order=>!seen.has(String(order.id)))];
      });
      setOrderPageInfo(result?.page||orderPageInfo);
    }catch(error){
      setDataLoadError(getUserErrorMessage(error,"이전 발주 내역을 불러오지 못했습니다."));
    }finally{
      setOrdersLoadingMore(false);
    }
  }

  async function loadUnreadNotificationCount(token=user?.token,userId=user?.id){
    if(!token||!userId){
      setUnreadNotificationCount(0);
      setUnreadOrderNotificationMap({});
      return 0;
    }

    try{
      const res=await fetch(`${DWORKS_API_BASE}/api/notifications/unread-count`,{
        headers:{Authorization:`Bearer ${token}`}
      });
      const data=await res.json().catch(()=>null);

      if(!res.ok||data?.ok===false){
        throw new Error(data?.message||data?.error||`HTTP ${res.status}`);
      }

      if(userId!==notificationOwnerRef.current) return 0;
      const count=Number(data?.unread_count||0);
      setUnreadNotificationCount(count);
      await loadUnreadOrderNotificationMap(token,userId);
      return count;
    }catch(error){
      if(userId===notificationOwnerRef.current) await loadUnreadOrderNotificationMap(token,userId);
      console.warn("알림 개수 조회 실패", error);
      return unreadNotificationCount;
    }
  }

  function getNotificationData(value){
    if(!value) return {};
    if(typeof value==="object") return value;
    try{
      const parsed=JSON.parse(value);
      return parsed&&typeof parsed==="object"?parsed:{};
    }catch{
      return {};
    }
  }

  async function loadUnreadOrderNotificationMap(token=user?.token,owner=user?.id){
    if(!token){
      setUnreadOrderNotificationMap({});
      return {};
    }

    try{
      const res=await fetch(`${DWORKS_API_BASE}/api/notifications?limit=80`,{
        headers:{Authorization:`Bearer ${token}`}
      });
      const data=await res.json().catch(()=>null);
      if(!res.ok||data?.ok===false) throw new Error(data?.message||data?.error||`HTTP ${res.status}`);

      if(owner!==notificationOwnerRef.current) return {};
      const next={};
      (Array.isArray(data?.notifications)?data.notifications:[]).forEach((notification)=>{
        if(notification?.is_read) return;
        const meta=getNotificationData(notification?.data);
        const orderId=String(meta?.order_id||notification?.order_id||"").trim();
        const threadId=String(meta?.thread_id||meta?.threadId||"").trim();
        if(!orderId||threadId||isChatNotification(notification)) return;

        const entry=next[orderId]||{count:0,notificationIds:[]};
        entry.count+=1;
        if(notification?.id) entry.notificationIds.push(String(notification.id));
        next[orderId]=entry;
      });

      setUnreadOrderNotificationMap(next);
      return next;
    }catch(error){
      if(owner!==notificationOwnerRef.current) return {};
      console.warn("발주별 미확인 알림 조회 실패",error);
      return unreadOrderNotificationMap;
    }
  }

  async function handleReadOrderNotifications(orderId){
    const safeOrderId=String(orderId||"").trim();
    const entry=unreadOrderNotificationMap[safeOrderId];
    const notificationIds=Array.isArray(entry?.notificationIds)?entry.notificationIds:[];
    if(!safeOrderId||notificationIds.length===0||!user?.token) return;

    const results=await Promise.allSettled(notificationIds.map(async(notificationId)=>{
      const finishNativeRead=prepareNativeNotificationRead(`/api/notifications/${encodeURIComponent(notificationId)}/read`,"PATCH");
      const res=await fetch(`${DWORKS_API_BASE}/api/notifications/${encodeURIComponent(notificationId)}/read`,{
        method:"PATCH",
        headers:{Authorization:`Bearer ${user.token}`}
      });
      if(!res.ok) throw new Error(`HTTP ${res.status}`);
      const readResult=await res.json().catch(()=>null);
      if(readResult?.ok!==false) finishNativeRead(readResult);
      return notificationId;
    }));
    const readIds=results.filter(result=>result.status==="fulfilled").map(result=>result.value);
    if(readIds.length===0) return;

    setUnreadOrderNotificationMap(prev=>{
      const current=prev[safeOrderId];
      if(!current) return prev;
      const remainingIds=(current.notificationIds||[]).filter(id=>!readIds.includes(id));
      const next={...prev};
      if(remainingIds.length===0) delete next[safeOrderId];
      else next[safeOrderId]={...current,count:remainingIds.length,notificationIds:remainingIds};
      return next;
    });
    setUnreadNotificationCount(prev=>Math.max(0,Number(prev||0)-readIds.length));
  }

  async function loadUnreadChatCount(token=user?.token){
    if(!token){
      setUnreadChatCount(0);
      return 0;
    }

    try{
      const response=await fetch(`${DWORKS_API_BASE}/api/buyer/chats/unread-count`,{
        headers:{Authorization:`Bearer ${token}`}
      });
      const data=await response.json().catch(()=>null);
      if(!response.ok||data?.ok===false){
        throw new Error(data?.message||data?.error||`HTTP ${response.status}`);
      }

      const count=Math.max(0,Number(data?.unread_count||0));
      setUnreadChatCount(count);
      return count;
    }catch(error){
      console.warn("채팅 미확인 개수 조회 실패",error);
      return unreadChatCount;
    }
  }


  function handleOpenNotificationOrder(orderId){
    setOrderEntry(null);
    const safeOrderId=String(orderId||"").trim();
    if(!safeOrderId){
      setPage("list");
      return;
    }

    setNotificationTargetThreadId("");
    setNotificationTargetOrderId(safeOrderId);
    setPage("list");
    loadUnreadNotificationCount(user?.token,user?.id);
  }

  function handleOpenNotificationChat(threadId,orderId=""){
    const safeThreadId=String(threadId||"").trim();
    if(!safeThreadId){
      handleOpenNotificationOrder(orderId);
      return;
    }

    setNotificationTargetOrderId("");
    setNotificationTargetThreadId(safeThreadId);
    setPage("chat");
    loadUnreadNotificationCount(user?.token,user?.id);
  }

  function handleTargetOrderHandled(){
    setNotificationTargetOrderId("");
    setNotificationTargetThreadId("");
  }

  async function getPaymentOrderAmount(token,orderId){
    if(!token||!orderId) return 0;

    try{
      const rows=await fetchBuyerPaymentOrders(token, {limit:1,providerOrderId:orderId});
      const order=rows.find(row=>row.provider_order_id===orderId);
      return Number(order?.amount)||0;
    }catch(error){
      console.warn("결제 금액 조회 실패", error);
    }

    return 0;
  }

  async function handlePaymentRedirect(){
    if(!BILLING_ENABLED) return;
    if(paymentHandledRef.current) return;
    if(typeof window==="undefined") return;
    if(screen!=="app"||!user?.token||!user?.id) return;

    const params=new URLSearchParams(window.location.search);
    const paymentResult=params.get("paymentResult");
    const paymentKey=params.get("paymentKey");
    // D-Works가 payment_orders.provider_order_id에 저장한 값은 dworksOrderId 기준입니다.
    // Toss가 함께 붙여주는 orderId보다 dworksOrderId를 우선 사용해야 DB 매칭이 안정적입니다.
    const orderId=params.get("dworksOrderId")||params.get("orderId");
    const failCode=params.get("code");
    const failMessage=params.get("message");

    const hasPaymentParams=!!paymentResult||!!paymentKey||!!orderId||!!failCode;
    if(!hasPaymentParams) return;

    paymentHandledRef.current=true;

    if(paymentResult==="fail"||failCode){
      const message=failMessage||"결제가 취소되었거나 실패했습니다.";
      setPaymentNotice(`결제가 완료되지 않았습니다. ${message}`);
      await postPaymentFail({orderId,code:failCode,message,token:user.token});
      alert(`결제가 완료되지 않았습니다.\n${message}`);
      clearPaymentQueryParams();
      return;
    }

    if(paymentResult&&paymentResult!=="success"&&!paymentKey){
      clearPaymentQueryParams();
      return;
    }

    if(!paymentKey||!orderId){
      setPaymentNotice("결제 승인에 필요한 정보가 부족합니다.");
      alert("결제 승인에 필요한 정보가 부족합니다. 다시 결제를 시도해주세요.");
      clearPaymentQueryParams();
      return;
    }

    let amount=Number(params.get("amount")||0);
    if(!amount){
      amount=await getPaymentOrderAmount(user.token,orderId);
    }

    if(!amount){
      setPaymentNotice("결제 금액 정보를 찾지 못했습니다.");
      alert("결제 금액 정보를 찾지 못했습니다. 다시 결제를 시도해주세요.");
      clearPaymentQueryParams();
      return;
    }

    setPaymentConfirming(true);
    setPaymentNotice("결제 승인 처리 중입니다.");

    try{
      await postPaymentConfirm({paymentKey,orderId,amount,token:user.token});

      setPaymentNotice("결제 승인이 완료되었습니다.");
      alert("결제가 완료되었습니다.\n요금제 전환 상태가 반영되었습니다.");

      clearPaymentQueryParams();
      await loadData(user.token);
      navigateBuyerPage("plans");
    }catch(error){
      console.warn("결제 승인 처리 실패", error);
      setPaymentNotice(getUserErrorMessage(error, "결제 승인 처리에 실패했습니다. 잠시 후 다시 시도해 주세요."));
      alert(`결제 승인 처리에 실패했습니다.\n${getUserErrorMessage(error, "잠시 후 다시 확인해 주세요.")}`);
      clearPaymentQueryParams();
    }finally{
      setPaymentConfirming(false);
    }
  }

  useEffect(()=>{
    handlePaymentRedirect();
  },[screen,user?.token,user?.id]);


  useEffect(()=>{
    if(screen!=="app"||!user?.token||!user?.id) return;

    let stopped=false;
    let refreshInFlight=false;
    let lastRefreshAt=0;
    const refreshIntervalMs=60000;
    const minimumRefreshGapMs=15000;

    const refreshUnread=async(force=false)=>{
      if(stopped||refreshInFlight) return;
      if(typeof document!=="undefined"&&document.visibilityState!=="visible") return;
      if(typeof navigator!=="undefined"&&navigator.onLine===false) return;

      const now=Date.now();
      if(!force&&now-lastRefreshAt<minimumRefreshGapMs) return;

      refreshInFlight=true;
      lastRefreshAt=now;
      try{
        await Promise.allSettled([
          loadUnreadNotificationCount(user.token,user.id),
          loadUnreadChatCount(user.token)
        ]);
      }finally{
        refreshInFlight=false;
      }
    };

    void refreshUnread(true);

    const intervalId=window.setInterval(()=>{ void refreshUnread(false); },refreshIntervalMs);
    const handleFocus=()=>{ void refreshUnread(false); };
    const handleVisibilityChange=()=>{
      if(document.visibilityState==="visible") void refreshUnread(false);
    };

    window.addEventListener("focus",handleFocus);
    document.addEventListener("visibilitychange",handleVisibilityChange);

    return ()=>{
      stopped=true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus",handleFocus);
      document.removeEventListener("visibilitychange",handleVisibilityChange);
    };
  },[screen,user?.token,user?.id]);

  useEffect(()=>{
    if(screen!=="app" || !user?.token || !user?.refreshToken) return;

    let stopped=false;
    const refreshIfNeeded=async(force=false)=>{
      if(stopped || (!force && !sessionNeedsRefresh(user))) return;
      try{
        const nextUser=await refreshCurrentSession();
        if(stopped || !nextUser?.token) return;
      }catch(error){
        console.warn("세션 자동 갱신 실패",error);
      }
    };

    refreshIfNeeded();
    const intervalId=window.setInterval(()=>refreshIfNeeded(),30000);
    const handleFocus=()=>refreshIfNeeded();
    const handleVisibilityChange=()=>{
      if(document.visibilityState==="visible") refreshIfNeeded();
    };
    window.addEventListener("focus",handleFocus);
    document.addEventListener("visibilitychange",handleVisibilityChange);

    return ()=>{
      stopped=true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus",handleFocus);
      document.removeEventListener("visibilitychange",handleVisibilityChange);
    };
  },[screen,user?.id,user?.token,user?.refreshToken,user?.expiresAt,user?.portalRole]);

  useEffect(()=>{
    if(
      screen!=="app" ||
      !user?.token ||
      isVendorAreaPath ||
      isOpsPath ||
      page==="ops" ||
      page==="ops-users" ||
      page==="ops-broadcast" ||
      typeof window==="undefined"
    ) return undefined;

    let cancelled=false;
    const preload=()=>{
      if(!cancelled) loadChatPage().catch(()=>{});
    };

    if(typeof window.requestIdleCallback==="function"){
      const idleId=window.requestIdleCallback(preload,{timeout:2000});
      return ()=>{
        cancelled=true;
        window.cancelIdleCallback?.(idleId);
      };
    }

    const timeoutId=window.setTimeout(preload,1200);
    return ()=>{
      cancelled=true;
      window.clearTimeout(timeoutId);
    };
  },[screen,user?.token,isVendorAreaPath,isOpsPath,page]);

  const [signupHandoff, setSignupHandoff] = useState(null);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let disposed = false, listener;
    const open = ({url} = {}) => {
      const recoveryCode = recoveryCodeFromUrl(url);
      if(!disposed && recoveryCode){
        window.location.replace(`/reset-password?code=${encodeURIComponent(recoveryCode)}`);
        return;
      }
      const code = signupCodeFromUrl(url);
      if (!disposed && code) window.location.replace(`/login?emailConfirmed=1&code=${encodeURIComponent(code)}`);
    };
    (async () => {
      listener = await SignupLink.addListener("appUrlOpen", open);
      if (disposed) { await listener.remove(); return; }
      open(await SignupLink.getLaunchUrl());
    })().catch(() => {});
    return () => { disposed = true; listener?.remove(); };
  }, []);

  useEffect(()=>{
    async function checkSession(){
      if(vendorConfirmToken || vendorInviteToken || vendorStaffInviteToken){
        setScreen("public");
        return;
      }

      try{
        const recoveryCode=recoveryCodeFromUrl(window.location.href);
        if(recoveryCode){
          const pending=readRecoveryPkce();
          if(!pending){
            const href=Capacitor.isNativePlatform()?null:`kr.dworks.app://auth/recovery?code=${encodeURIComponent(recoveryCode)}`;
            setSignupHandoff({href,recovery:true,error:Capacitor.isNativePlatform()?"재설정 요청 정보를 찾지 못했습니다. 이 앱에서 메일을 다시 요청해 주세요.":"D-Works 앱으로 돌아가 새 비밀번호를 입력해 주세요. 앱이 없다면 웹에서 재설정 메일을 다시 요청해 주세요."});
            if(href) window.location.assign(href);
            return;
          }
          try{
            const auth=await exchangeSignupOnce(recoveryCode,pending.verifier);
            if(!auth?.access_token || auth.user?.email?.toLowerCase()!==pending.email) throw new Error("재설정 링크가 만료되었거나 요청 정보와 일치하지 않습니다. 메일을 다시 요청해 주세요.");
            clearRecoveryPkce();
            try{localStorage.removeItem("dworks_session");}catch{}
            setUser(null);setResetRecoveryAudience("buyer");
            setResetRecoveryToken(auth.access_token);setResetRecoveryError("");
          }catch(error){setResetRecoveryToken("");setResetRecoveryError(error.message || "재설정 인증에 실패했습니다.");}
          window.history.replaceState({},document.title,"/reset-password");
          setAuthPath("/reset-password");setScreen("reset-password");return;
        }
        const signupCode = signupCodeFromUrl(window.location.href);
        if (signupCode) {
          const pending = readSignupPkce();
          if (!pending) {
            const href = `kr.dworks.app://auth/callback?code=${encodeURIComponent(signupCode)}`;
            setSignupHandoff({href: Capacitor.isNativePlatform() ? null : href, error: ""});
            if (!Capacitor.isNativePlatform()) window.location.assign(href);
            return;
          }
          try {
            const auth = await exchangeSignupOnce(signupCode, pending.verifier);
            if (!auth?.access_token || !auth?.user?.email_confirmed_at || auth.user.email?.toLowerCase() !== pending.email) {
              throw new Error("인증 링크가 만료되었거나 가입 정보와 일치하지 않습니다. 다시 로그인해 주세요.");
            }
            clearSignupPkce();
            window.history.replaceState({}, document.title, "/login");
            const meta = auth.user.user_metadata || {};
            await handleLogin(normalizeAuthSession({id:auth.user.id, email:auth.user.email,
              name:meta.name || "", company:meta.company || "", tel:meta.tel || "", brand:meta.brand || "",
              position:meta.position || "", address:meta.address || ""}, auth), true);
          } catch (error) {
            setSignupHandoff({href:null,error:error.message || "자동 로그인에 실패했습니다. 다시 로그인해 주세요."});
          }
          return;
        }
        const recovery=readPasswordRecoveryFromUrl();
        if(recovery.isRecovery){
          try{localStorage.removeItem("dworks_session");}catch{}
          setUser(null);
          setResetRecoveryAudience(recovery.audience || "buyer");
          window.history.replaceState({},document.title,"/reset-password");
          setAuthPath("/reset-password");

          if(recovery.error){
            setResetRecoveryToken("");
            setResetRecoveryError(recovery.errorDescription || "비밀번호 재설정 링크가 만료되었거나 유효하지 않습니다.");
            setScreen("reset-password");
            return;
          }

          if(recovery.accessToken){
            setResetRecoveryToken(recovery.accessToken);
            setResetRecoveryError("");
            setScreen("reset-password");
            return;
          }

          setResetRecoveryToken("");
          setResetRecoveryError("비밀번호 재설정 인증 정보가 없습니다. 재설정 메일을 다시 요청해 주세요.");
          setScreen("reset-password");
          return;
        }

        const sessionKey=isVendorAreaPath?"dworks_vendor_session":"dworks_session";
        const s=localStorage.getItem(sessionKey);
        if(s){
          let u=normalizeAuthSession(JSON.parse(s));
          if(u?.token){
            if(sessionNeedsRefresh(u)){
              u=await refreshStoredSession(u,sessionKey);
            }
            let r=await fetch(`${SB}/auth/v1/user`,{headers:{"apikey":KEY,"Authorization":`Bearer ${u.token}`}});
            if(!r.ok && u.refreshToken){
              u=await refreshStoredSession(u,sessionKey);
              r=await fetch(`${SB}/auth/v1/user`,{headers:{"apikey":KEY,"Authorization":`Bearer ${u.token}`}});
            }
            if(r.ok){
              if(isVendorAreaPath){
                try{
                  const portalData=await fetchVendorPortalMe(u.token);
                  const vendorAccount=portalData?.account||portalData?.vendor_account||null;
                  if(!vendorAccount) throw new Error("NO_VENDOR_ACCOUNT");
                }catch(error){
                  console.warn("공급처 저장 세션 확인 실패", error);
                  const lookupFailed=error?.code==="VENDOR_PORTAL_ACCOUNT_LOOKUP_FAILED";
                  setVendorRecoveryError(String(error?.code||"").startsWith("VENDOR_PORTAL_") ? error.message : "");
                  if(!lookupFailed){try{localStorage.removeItem("dworks_vendor_session");}catch{}}
                  setUser(null);
                  window.history.replaceState({},document.title,"/vendor/login");
                  setScreen("vendor-auth");
                  return;
                }

                setAccountAccessInfo(null);
                setVendorPortalReady(false);
                setUser(u);
                setScreen("app");

                if(isVendorLoginPath){
                  window.history.replaceState({},document.title,"/vendor/portal");
                }
                return;
              }

              u={...u,accountType:await getAccountType(u.token,u.id)};
              if(u.accountType!=="buyer" && u.accountType!=="ops_admin"){
                await rejectBuyerEntry(u);
                return;
              }
              u=await restoreBuyerProfile(u,await r.json());
              try{
                if(localStorage.getItem("dworks_session")){
                  localStorage.setItem("dworks_session",JSON.stringify(u));
                }
              }catch{}

              if(isOpsAdminUser(u)){
                setAccountAccessInfo(null);
                setUser(u);
                setPage("ops");
                window.history.replaceState({},document.title,"/ops");
                setAuthPath("/ops");
                setScreen("app");
                return;
              }

              const accessInfo=await getAccountAccessStatus(u.token,u.id);
              if(accessInfo.blocked){
                try{localStorage.removeItem("dworks_session");}catch{}
                setUser(u);
                setAccountAccessInfo(accessInfo);
                setScreen("account-blocked");
                return;
              }

              setAccountAccessInfo(null);
              setUser(u);
              if(AUTH_PATHS.has(currentPath)){
                window.history.replaceState({},document.title,"/");
                setAuthPath("/");
              }
              setScreen("app");
              if(isOpsPath){
                setPage(getOpsPageFromPath(currentPath) || "ops");
              }
              const initialBuyerPage=getBuyerPageFromPath(currentPath);
              if(buyerPageNeedsBootstrap(initialBuyerPage,notificationTargetOrderId)){
                void loadData(u.token);
              }
              return;
            }
          }
        }
      }catch{}
      if(isVendorAreaPath){
        if(isVendorPortalPath){
          try{sessionStorage.setItem("dworks_vendor_return_path",currentPath);}catch{}
          window.history.replaceState({},document.title,"/vendor/login");
        }
        setScreen("vendor-auth");
        return;
      }
      if(isOpsPath){
        setScreen("auth");
        return;
      }
      if(currentPath==="/reset-password"){
        setResetRecoveryToken("");
        setResetRecoveryError("비밀번호 재설정 링크가 만료되었거나 인증 정보가 없습니다. 재설정 메일을 다시 요청해 주세요.");
        setAuthPath("/reset-password");
        setScreen("reset-password");
        return;
      }
      if(currentPath==="/login"||currentPath==="/join"||currentPath==="/find-email"||currentPath==="/find"){
        setAuthPath(currentPath);
        setScreen("auth");
        return;
      }
      if(currentPath!=="/intro"){
        window.history.replaceState({},document.title,"/intro");
      }
      setAuthPath("/intro");
      setScreen("splash");
    }
    checkSession();
  },[]);

  async function handleNetworkReconnect(){
    if(!user?.token || isVendorAreaPath || reconnectRefreshPromiseRef.current) return;

    const refreshTasks=[
      loadUnreadNotificationCount(user.token,user.id),
      loadUnreadChatCount(user.token)
    ];
    if(buyerDataReadyRef.current || buyerPageNeedsBootstrap(page,notificationTargetOrderId)){
      refreshTasks.unshift(loadData(user.token));
    }

    reconnectRefreshPromiseRef.current=Promise.allSettled(refreshTasks).finally(()=>{
      reconnectRefreshPromiseRef.current=null;
    });

    await reconnectRefreshPromiseRef.current;
  }

  async function rejectBuyerEntry(authUser){
    const message=authUser.accountType==="vendor"
      ? "이 계정은 공급처 전용입니다. 공급처 로그인을 이용해 주세요."
      : authUser.accountType==="factory"
        ? "이 계정은 공장 전용입니다. 공장 로그인을 이용해 주세요."
        : "발주자 권한을 확인할 수 없습니다. 잠시 후 다시 시도하거나 D-Works에 문의해 주세요.";

    try{localStorage.removeItem("dworks_session");}catch{}
    setUser(null);
    setAccountAccessInfo(null);
    window.history.replaceState({},document.title,"/login");
    setAuthPath("/login");
    setScreen("auth");

    let logoutFailed=false;
    try{
      const response=await fetch(`${SB}/auth/v1/logout?scope=local`,{
        method:"POST",
        headers:{
          apikey:KEY,
          Authorization:`Bearer ${authUser.token}`
        }
      });
      logoutFailed=!response.ok;
    }catch{
      logoutFailed=true;
    }

    window.alert(message + (logoutFailed
      ? "\n화면 접근은 차단했지만 서버 로그아웃을 완료하지 못했습니다. 네트워크 연결을 확인해 주세요."
      : ""));
  }

  async function handleLogin(u, keep){
    const nextUser={...u,accountType:await getAccountType(u.token,u.id)};
    if(nextUser.accountType!=="buyer" && nextUser.accountType!=="ops_admin"){
      await rejectBuyerEntry(nextUser);
      return;
    }
    const opsAdmin=isOpsAdminUser(nextUser);
    const accessInfo=opsAdmin
      ? {blocked:false,status:"active",plan:null,memo:""}
      : await getAccountAccessStatus(nextUser.token,nextUser.id);

    if(accessInfo.blocked){
      try{localStorage.removeItem("dworks_session");}catch{}
      setUser(nextUser);
      setAccountAccessInfo(accessInfo);
      setScreen("account-blocked");
      return;
    }

    setAccountAccessInfo(null);

    if(keep) {
      try{localStorage.setItem("dworks_session",JSON.stringify(nextUser));}catch{}
    } else {
      try{localStorage.removeItem("dworks_session");}catch{}
    }
    setUser(nextUser);
    setNotificationTargetOrderId("");
    setNotificationTargetThreadId("");

    if(opsAdmin){
      setPage("ops");
      window.history.replaceState({},document.title,"/ops");
      setAuthPath("/ops");
    }else if(isOpsPath){
      const nextOpsPage=getOpsPageFromPath(currentPath) || "ops";
      setPage(nextOpsPage);
      window.history.replaceState({},document.title,nextOpsPage==="ops-users"?"/ops/users":nextOpsPage==="ops-broadcast"?"/ops/broadcast":"/ops");
      setAuthPath("/ops");
    }else{
      setPage("dash");
      window.history.replaceState({},document.title,"/");
      setAuthPath("/");
    }
    setScreen("app");

    if(isVendorPortalPath || opsAdmin){
      return;
    }

    void loadData(nextUser.token);
  }

  async function handleLogout(){
    try{clearProductCameraDraft();}catch{}
    try{await deactivateNativePush(user, { preservePreference: true });}catch{}
    try{if(user?.token) await DB.signOut(user.token);}catch{}
    try{localStorage.removeItem("dworks_session");}catch{}
    setAccountAccessInfo(null);
    setUnreadNotificationCount(0);
    setUnreadOrderNotificationMap({});
    dashboardStatsRequestSeqRef.current+=1;
    buyerDataReadyRef.current=false;
    buyerDataLoadPromiseRef.current=null;
    setDashboardStats(null);
    setRecentOrderHistory([]);
    setUser(null);
    window.history.replaceState({},document.title,"/login");
    setAuthPath("/login");
    setScreen("auth");
  }

  function handleVendorLogin(u,keep){
    if(keep){
      try{localStorage.setItem("dworks_vendor_session",JSON.stringify(u));}catch{}
    }else{
      try{localStorage.removeItem("dworks_vendor_session");}catch{}
    }

    setAccountAccessInfo(null);
    setVendorPortalReady(false);
    setUser(u);
    let nextVendorPath="/vendor/portal";
    try{
      const savedPath=sessionStorage.getItem("dworks_vendor_return_path");
      if(savedPath&&Object.prototype.hasOwnProperty.call(vendorPortalTabByPath,savedPath)){
        nextVendorPath=savedPath;
      }
      sessionStorage.removeItem("dworks_vendor_return_path");
    }catch{}
    window.history.replaceState({},document.title,nextVendorPath);
    setScreen("app");
  }

  async function handleVendorLogout(){
    try{await deactivateNativePush(user, { preservePreference: true });}catch{}
    try{if(user?.token) await DB.signOut(user.token);}catch{}
    try{localStorage.removeItem("dworks_vendor_session");}catch{}
    setAccountAccessInfo(null);
    setVendorPortalReady(false);
    setUser(null);
    window.history.replaceState({},document.title,"/vendor/login");
    setScreen("vendor-auth");
  }

  function handleGoBuyerLogin(){
    setUser(null);
    window.history.replaceState({},document.title,"/login");
    setAuthPath("/login");
    setScreen("auth");
  }

  function handleGoVendorLogin(){
    window.location.assign("/vendor/login");
  }

  function handleGoVendorSignup(){
    window.location.assign("/vendor/signup");
  }

  function handleAppLogoClick(){
    if(page==="ops" || page==="ops-users" || page==="ops-broadcast" || isOpsPath){
      setPage("ops");
      if(typeof window!=="undefined" && window.location.pathname!=="/ops"){
        window.history.replaceState({},document.title,"/ops");
      }
      return;
    }

    setPage("dash");
    if(typeof window!=="undefined" && window.location.pathname!=="/"){
      window.history.replaceState({},document.title,"/");
    }
  }

  if (signupHandoff) return <main style={{maxWidth:480,margin:"80px auto",padding:24,lineHeight:1.7}}>
    <h1>{signupHandoff.recovery?"비밀번호 재설정":"이메일 인증"}</h1>
    <p>{signupHandoff.error || "가입한 앱으로 돌아가 로그인을 완료해 주세요. 다른 기기에서 인증했거나 앱을 다시 설치했다면 직접 로그인해 주세요."}</p>
    {signupHandoff.href && <p><a href={signupHandoff.href}>D-Works 앱 열기</a></p>}
    <a href="/login">로그인 화면으로 이동</a>
  </main>;

  if(vendorConfirmToken){
    return <VendorConfirmPage token={vendorConfirmToken}/>;
  }

  if(vendorStaffInviteToken){
    return (
      <VendorStaffJoinPage
        inviteToken={vendorStaffInviteToken}
        onGoLogin={handleGoVendorLogin}
      />
    );
  }

  if(vendorInviteToken){
    return (
      <VendorJoinPage
        inviteToken={vendorInviteToken}
        onJoined={handleVendorLogin}
        onGoLogin={handleGoVendorLogin}
      />
    );
  }

  if(isVendorSignupPath){
    return (
      <VendorSignupPage
        onJoined={handleVendorLogin}
        onGoLogin={handleGoVendorLogin}
      />
    );
  }

  if(isVendorAreaPath && screen==="loading"){
    return (
      <div className="app-loading-screen" role="status" aria-live="polite">
        <span className="app-loading-screen__spinner" aria-hidden="true"/>
        <span className="app-loading-screen__label">공급처 화면을 준비하는 중입니다.</span>
      </div>
    );
  }

  if(isVendorLoginPath || (isVendorPortalPath && (screen!=="app" || !user))){
    return (
      <VendorLoginPage
        recoveryError={vendorRecoveryError}
        onLogin={handleVendorLogin}
        onGoBuyerLogin={handleGoBuyerLogin}
        onGoSignup={handleGoVendorSignup}
      />
    );
  }

  if(isVendorPortalPath && screen==="app" && user){
    return (
      <React.Suspense fallback={
        <div className="app-loading-screen" role="status" aria-live="polite">
          <span className="app-loading-screen__spinner" aria-hidden="true"/>
          <span className="app-loading-screen__label">공급처 화면을 불러오는 중입니다.</span>
        </div>
      }>
        <NetworkStatusBanner/>
        <NativePushBridge user={user}/>
        <VendorPortalPage
          user={user}
          initialTab={vendorPortalInitialTab}
          onLogout={handleVendorLogout}
          onReady={()=>setVendorPortalReady(true)}
        />
        <BetaFeedback user={user} surface="vendor"/>
        <FeedbackUpdates user={user} surface="vendor"/>
        {!vendorPortalReady&&(
          <div className="app-loading-screen" role="status" aria-live="polite">
            <span className="app-loading-screen__spinner" aria-hidden="true"/>
            <span className="app-loading-screen__label">공급처 화면을 준비하는 중입니다.</span>
          </div>
        )}
      </React.Suspense>
    );
  }

  const TABS=[
    {k:"order", l:"발주하기"},
    {k:"prods", l:"상품리스트"},
    {k:"list", l:"발주리스트"},
    {k:"chat", l:"채팅"},
    {k:"settings", l:"설정"}
  ];

  if(screen==="loading")return(
    <div className="app-loading-screen" role="status" aria-live="polite">
      <span className="app-loading-screen__spinner" aria-hidden="true"/>
      <span className="app-loading-screen__label">로딩 중...</span>
    </div>
  );
  if(screen==="splash")return<SplashPage onStart={()=>navigateAuth("/login")}/>;
  if(screen==="account-blocked")return(
    <AccountBlockedPage
      user={user}
      accessInfo={accountAccessInfo}
      onLogout={handleLogout}
    />
  );
  if(screen==="reset-password")return(
    <ResetPasswordPage
      recoveryToken={resetRecoveryToken}
      recoveryError={resetRecoveryError}
      onDone={async(auth)=>{
        try{localStorage.removeItem("dworks_session");}catch{}
        setResetRecoveryToken("");
        setResetRecoveryError("");
        setUser(null);
        if(auth?.user && resetRecoveryAudience==="buyer"){
          const meta=auth.user.user_metadata||{};
          try{
            await handleLogin(normalizeAuthSession({id:auth.user.id,email:auth.user.email,name:meta.name||"",company:meta.company||"",tel:meta.tel||""},auth),true);
            return;
          }catch{alert("비밀번호가 변경되었습니다. 다시 로그인해 주세요.");}
        }
        const nextPath=resetRecoveryAudience==="vendor"?"/vendor/login":resetRecoveryAudience==="factory"?"/factory/login":"/login";
        window.history.replaceState({},document.title,nextPath);
        if(resetRecoveryAudience==="factory"){
          window.location.assign("/factory/login");
        }else if(resetRecoveryAudience==="vendor"){
          setScreen("vendor-auth");
        }else{
          setAuthPath("/login");
          setScreen("auth");
        }
      }}
      onBack={()=>{
        clearPasswordRecoveryUrl();
        setResetRecoveryToken("");
        setResetRecoveryError("");
        setUser(null);
        const nextPath=resetRecoveryAudience==="vendor"?"/vendor/login":resetRecoveryAudience==="factory"?"/factory/login":"/login";
        window.history.replaceState({},document.title,nextPath);
        if(resetRecoveryAudience==="factory"){
          window.location.assign("/factory/login");
        }else if(resetRecoveryAudience==="vendor"){
          setScreen("vendor-auth");
        }else{
          setAuthPath("/login");
          setScreen("auth");
        }
      }}
    />
  );
  if(screen!=="app"||!user)return(
    <AuthPage
      onLogin={handleLogin}
      authPath={authPath}
      onNavigate={(pathName)=>navigateAuth(pathName)}
    />
  );

  // Both entry routes render the same center and ChatPage instance.
  const notificationCenter=(<NotificationsPage
      key={user?.id}
      user={user}
      activeTab={page==="chat"?"chat":"notifications"}
      onTabChange={tab=>navigateBuyerPage(tab==="chat"?"chat":"notifications")}
      chatUnreadCount={unreadChatCount}
      chatContent={<ChatPage
      user={user}
      orders={orders}
      targetOrderId={notificationTargetOrderId}
      targetThreadId={notificationTargetThreadId}
      onTargetOrderHandled={handleTargetOrderHandled}
      onUnreadChange={setUnreadChatCount}
      onNotificationsRead={()=>loadUnreadNotificationCount(user.token,user.id)}
      onRefreshSession={refreshCurrentSession}
    />}
      onNav={navigateBuyerPage}
      onUnreadChange={()=>{loadUnreadNotificationCount(user?.token,user?.id);loadUnreadChatCount(user?.token);}}
      onOpenOrder={handleOpenNotificationOrder}
      onOpenChat={handleOpenNotificationChat}
    />);

  const pages={
    dash:<DashPage orders={orders} dashboardStats={dashboardStats} products={products} vendors={vendors} factories={factories} user={user} dataLoading={loading} onNav={navigateBuyerPage} onOpenOrders={query=>navigateBuyerPage("list","account",query)}/>,
    order:<OrderPage products={products} orders={orders} recentOrderSource={recentOrderHistory} setOrders={setOrders} vendors={vendors} factories={factories} brandClients={brandClients} user={user} dataLoading={loading} dataLoadError={dataLoadError} onRetryLoad={()=>loadData(user.token)} onNav={navigateBuyerPage} quickOrderRequest={quickOrderRequest}/>,
    prods:<ProdsPage products={products} setProducts={setProducts} vendors={vendors} setVendors={setVendors} factories={factories} setFactories={setFactories} brandClients={brandClients} user={user} dataLoading={loading} onLoadProductImage={loadProductImage} resetKey={productListResetKey} createRequest={productCreateRequest} onCreateRequestHandled={()=>setProductCreateRequest(null)} onCreateCancelled={()=>{setProductCreateRequest(null);navigateBuyerPage("dash");}} onQuickOrder={startQuickOrder}/>,
    list:<ListPage
      key={JSON.stringify(orderEntry)}
      initialQuery={orderEntry}
      orders={orders}
      setOrders={setOrders}
      dataLoading={loading||ordersFiltering||ordersBootstrapLoading}
      dataLoadError={dataLoadError||ordersBootstrapError}
      onOrderFiltersChange={reloadBuyerOrders}
      activeOrderQuery={buyerOrderQueryRef.current}
      onRetryLoad={()=>loadData(user.token)}
      orderPageInfo={orderPageInfo}
      orderStats={orderStats}
      onFeedbackStateChange={refreshFeedbackOrderStats}
      ordersLoadingMore={ordersLoadingMore}
      onLoadMoreOrders={loadMoreBuyerOrders}
      products={products}
      vendors={vendors}
      brandClients={brandClients}
      user={user}
      onNav={navigateBuyerPage}
      targetOrderId={notificationTargetOrderId}
      onTargetOrderHandled={handleTargetOrderHandled}
      unreadOrderNotificationMap={unreadOrderNotificationMap}
      onReadOrderNotifications={handleReadOrderNotifications}
    />,
    chat:notificationCenter,
    inventory:<InventoryPage user={user} factories={factories} vendors={vendors} products={products} orders={orders} onSettingsNavigate={navigateSettingsSection}/>,
    vendors:<VendorPage vendors={vendors} setVendors={setVendors} user={user} dataLoading={loading} onSettingsNavigate={navigateSettingsSection}/>,
    more:<MorePage user={user} onNav={navigateBuyerPage} onSettingsNavigate={navigateSettingsSection} onOpenOrders={query=>navigateBuyerPage("list","account",query)}/>,
    subscription:<SubscriptionPage user={user} onNav={navigateBuyerPage}/>,
    faq:<BuyerFaqPage onNav={navigateBuyerPage}/>,
    guide:<BuyerGuidePage onNav={navigateBuyerPage}/>,
    settings:<SettingsPage user={user} setUser={setUser} vendors={vendors} factories={factories} setFactories={setFactories} brandClients={brandClients} setBrandClients={setBrandClients} onLogout={handleLogout} onNav={navigateBuyerPage} settingsSection={settingsSection} setSettingsSection={setSettingsSection} onSettingsNavigate={navigateSettingsSection}/>,
    plans:BILLING_ENABLED && PlansPage
      ? <PlansPage user={user} onNav={navigateBuyerPage}/>
      : <SettingsPage user={user} setUser={setUser} vendors={vendors} factories={factories} setFactories={setFactories} brandClients={brandClients} setBrandClients={setBrandClients} onLogout={handleLogout} onNav={navigateBuyerPage} settingsSection={settingsSection} setSettingsSection={setSettingsSection} onSettingsNavigate={navigateSettingsSection}/>,
    notifications:notificationCenter,
    ops:isOpsAdminUser(user)
      ? <OpsDashboardPage user={user} onNav={navigateBuyerPage}/>
      : <div >
          <div >
            <div >운영 대시보드 접근 제한</div>
            <div >관리자 계정만 접근할 수 있습니다.</div>
          </div>
        </div>,
    "ops-users":isOpsAdminUser(user)
      ? <OpsUsersPage user={user}/>
      : <div><strong>관리자 계정만 접근할 수 있습니다.</strong></div>,
    "ops-broadcast":isOpsAdminUser(user)
      ? <OpsBroadcastPage user={user}/>
      : <div><strong>관리자 계정만 접근할 수 있습니다.</strong></div>,
  };

  const unreadOrderNotificationCount=Object.values(unreadOrderNotificationMap).reduce(
    (sum,entry)=>sum+Math.max(0,Number(entry?.count||0)),
    0
  );

  const buyerNav=[
    {key:"dash",label:"대시보드"},
    // 채팅 미확인 수는 채팅 메뉴에, 발주 처리 알림은 내역 메뉴에 표시합니다.
    ...TABS.map(({k,l})=>({
      key:k==="settings"?"more":k,
      label:k==="settings"?"더보기":l,
      badgeCount:k==="chat"?unreadChatCount:k==="list"?unreadOrderNotificationCount:0
    }))
  ];

  const opsMode=isOpsPath||page==="ops"||page==="ops-users"||page==="ops-broadcast";
  const opsNav=[
    {key:"ops",label:"운영 대시보드"},
    {key:"ops-users",label:"회원 관리"},
    {key:"ops-broadcast",label:"전체 공지 챗"}
  ];

  return(
    <AppLayout
      buyer={!opsMode}
      actualPage={page}
      bottomItems={opsMode?undefined:[
        {key:"product-create",label:"상품 등록",icon:"product-create",onClick:()=>{setProductCreateRequest(Date.now());navigateBuyerPage("prods");}},
        {key:"order",label:"신상 발주"},
        {key:"list",label:"발주목록",badgeCount:unreadOrderNotificationCount},
        {key:"more",label:"더보기",icon:"more"}
      ]}
      items={opsMode?opsNav:buyerNav}
      activePage={["settings","inventory","vendors","subscription","faq","guide"].includes(page)?"more":page}
      onNavigate={navigateBuyerPage}
      onLogoClick={handleAppLogoClick}
      onSettings={()=>navigateSettingsSection("account")}
      user={user}
      unreadCount={opsMode?0:unreadNotificationCount}
      onNotifications={opsMode?handleLogout:()=>navigateBuyerPage("notifications")}
      footerAriaLabel={opsMode?"로그아웃":"알림 페이지로 이동"}
      footerIcon={opsMode?"logout":"notification"}
    >
        <NetworkStatusBanner onReconnect={handleNetworkReconnect}/>
        {!opsMode&&<NativePushBridge user={user}/>}
        {BILLING_ENABLED&&(paymentConfirming||paymentNotice)&&(
          <div >
            {paymentNotice||"결제 승인 처리 중입니다."}
          </div>
        )}

        {pages[page]||(opsMode?pages["ops"]:pages["dash"])}
    </AppLayout>
  );
}



